// @ts-strict-ignore
/* eslint-disable no-prototype-builtins */

import { Subject, Subscription } from 'rxjs';
import { CallMedia, CallRB, CallStatus, CallType } from 'rainbow-web-sdk/models/call.model';

import { Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { WebrtcConfigAndUtils } from 'rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils';
import type { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { TELEPHONY_SVC } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { WebrtcP2PService, WebrtcP2PServiceEvents, WEBRTC_P2P_SVC } from "rainbow-web-sdk/services/webrtc/webrtcP2P.service";
import { SettingsService } from 'rainbow-web-sdk/services/core';
import { ConversationServiceRB } from '../conversation/conversation.service';
import { ConversationEvents } from 'rainbow-web-sdk/models';
import { format } from 'date-fns';
import { SUPERVISION_SVC } from '../telephony/supervision.service';
import type { SupervisionService } from '../telephony/supervision.service';
import { CALL_SVC } from 'rainbow-web-sdk/services/call/call.service';
import { Contact } from '../../models/contact.model';
import { $iq } from 'strophe.js';
import { UserServiceEvents, UserServiceRB } from '../users/user.service';

const ITERATION_POLLING_SHORT = 2; //CRRAINB-24110
const TO_PILLAR_POLLING_SHORT = 15000;//CRRAINB-24110
const TO_PILLAR_POLLING_MIN = 300000;
const TO_PILLAR_POLLING_MAX = 900000;
const TO_PILLAR_POLLING_STEP = TO_PILLAR_POLLING_MAX / 500;

export const WEBRTC_GATEWAY_SVC = 'WebrtcGatewayService';

export enum MPCallState {
    Unknow = "Unknown",
    Free = "Free",
    WaitWebrtc = "WaitWebrtc",
    WaitTelephony = "WaitTelephony",
    CallOffer = "CallOffer",
    CallOnGoing = "CallOnGoing",
    CallActive = "CallActive",
    CallActiveNoWebMedia = "CallActiveNoWebMedia",
    TelCallReleasing = "TelCallReleasing",
    WebCallReleasing = "WebCallReleasing",
    RemoteControled = "RemoteControled",
    AnomalyCCS = "AnomalyCCS"
}

interface MediaPillarStates {
    mediaPillarConfigured: boolean;
    mediaPillarAlive: boolean;
}
// Media Pillar connection interface
interface MediaPillarConnection {
    keepAlive_TO: any; //Time Out for keepAlive
    mediaPillarJidArray: string[];
    rainbowPhoneNumber: string;
    remoteExtension: string;
    mediaPillarStatesPool: Partial<MediaPillarStates>;
}

// Media Pillar call context shell interface
interface MediaPillarCallContext {
    id: number;
    callState: MPCallState;
    callState_TO: any; //Time Out for callstate/transition
    waitTelephony_TO: any; //Time Out for waitTelephony transition,
    isOutgoingCall3PCC: boolean; //if outgoing call
    telephonyCallRefs: any[]; //tab of refs associated to telephonic calls
    webrtcCallRef: any; //pointer to associated webrtc call
    previousCallConnectionId: string; //the previously received call id in state machine
    previousCallStatus: string; //the previously received call status in state machine
    updateContactFlag: boolean; //CCRAINB-4634
    getMediaPillarJid: any;
    onGoingCallMediaPillarJid: string;//RQRAINB-6670
    callOwnerForOxoMultiClientFlag: boolean; //RQ6394
}

export class WebrtcGatewayService extends Service {

    private portalURL: string;
    public started: boolean = false;
    private listeners: any[] = [];
    private myContact = null;
    private mediaPillarContact = null; // 1PCC CPaaS
    private mediaPillarConfigured = false;
    private mediaPillarAlive = false;
    private evtQueue = [];
    private SEM = 0;
    private mediaPillarPollingSelected = false;

    private TO_transition = 15000; //Time Out transition for debug set to 3600000 //CRRAINB-3271 pass from 10s to 15s
    private TO_transition_long = 30000; //Time Out transition for long case //CRRAINB-3271
    private TO_NoWebMedia = 60000; //Time Out for webmedia lost 1mn //CRRAINB-3500
    private TO_NoWebMediaNotel = 5000; //Time Out for webmedia lost 5s //CRRAINB-6895
    private TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN; //Polling timer
    private TO_waitTelephony = 600; // short Time Out transition waiting telephony
    private mediaPillarKeepAliveSuspend = false; //Polling trigger CRRAINB-3757
    private keepAliveIterationCpt = 0; //CRRAINB-24110

    public mediaPillarConnection: MediaPillarConnection;

    // Media Pillar call context shell (only one shell used at once)
    public mediaPillarCallContext: MediaPillarCallContext;
    public mediaPillarCallContexts: MediaPillarCallContext[] = [];

    public mediaPillarCallContextId = 1;

    private isMixPstnWebRtcAllowed: boolean = false;

    private systemId: string;
    private displayName: string;
    private oxeBusyCapability: boolean;
    private systDeviceName: string;

    // ********************************************************* //
    // ** LIFECYCLE STUFF                                      * //
    // ********************************************************* //
    private startRetryTimer: any;
    private xmppConnectionSubscription: Subscription;
    private subscriptions: Subscription[] = [];
    public rxSubject: Subject<any> = new Subject<any>();
    private mainService: MainService = MainService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private userService: UserServiceRB = UserServiceRB.getInstance();
    private webrtcP2PService: WebrtcP2PService = undefined;
    public telephonyService: TelephonyService = undefined;
    private supervisionService: SupervisionService = undefined;

    public static getInstance() {
        return Service.create(WEBRTC_GATEWAY_SVC, WebrtcGatewayService);
    }

    public static build() {
        return new WebrtcGatewayService();
    }

    protected constructor() { super(); }

    /**
     * Start service method
     * @public
     * @async
     * @memberof WebrtcGatewayService
     */
    public async start(): Promise<void> {
        this.setMediaPillarConfigured(false);
        this.mediaPillarAlive = false;
        this.portalURL = config.restServerUrl + "/api/rainbow/mediapillar/v1.0/mediapillars";
        this.myContact = this.contactService.userContact;
        this.mediaPillarContact = null; // 1PCC CPaaS
        this.mediaPillarPollingSelected = false;

        this.mediaPillarConnection = {
            keepAlive_TO: null,
            mediaPillarJidArray: [],
            rainbowPhoneNumber: "",
            remoteExtension: "",
            mediaPillarStatesPool: {},
        };

        this.mediaPillarCallContext = {
            id: 0,
            callState: MPCallState.Free,
            callState_TO: null,
            waitTelephony_TO: null,
            isOutgoingCall3PCC: false,
            telephonyCallRefs: [],
            webrtcCallRef: null,
            previousCallConnectionId: null,
            previousCallStatus: null,
            updateContactFlag: false,
            onGoingCallMediaPillarJid: "", //RQRAINB-6670
            callOwnerForOxoMultiClientFlag: false, //RQ6394
            getMediaPillarJid: () => { return this.getOnGoingMediaPillarJid(); } //RQRAINB-6670
            //getMediaPillarJid: () => { return this.mediaPillarConnection.mediaPillarJid; }
        };
        this.mediaPillarCallContexts = [];//empty contexts table

        this.isMixPstnWebRtcAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS_FOR_PBX) || config.mixwebrtcpstn || config.mixwebrtcpstnhybride;

        this.webrtcP2PService = this.mainService.inject<WebrtcP2PService>(WEBRTC_P2P_SVC);
        this.telephonyService = this.mainService.inject<TelephonyService>(TELEPHONY_SVC);
        this.supervisionService = this.mainService.inject<SupervisionService>(SUPERVISION_SVC);

        this.systemId = this.contactService.userContact.systemId;
        this.displayName = this.contactService.userContact.displayName;
        this.oxeBusyCapability = this.contactService.userContact.oxeBusyCapability;
        this.systDeviceName = this.contactService.userContact.systDeviceName;

        // Start the service only if the media pillar feature is available
        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_WEBRTC_PSTN_CALLING)
            && this.myContact.systemId) {

            this.logger.info("[WebrtcGatewayService] === STARTING ===");

            this.started = true;
            this.SEM = 0;
            this.resetEvtAutomaton();
            this.mediaPillarKeepAliveSuspend = false;

            // Get the mediaPillar JID
            try {
                const mediaPillarJidArray = await this.getMediaPillarData();
                if (!mediaPillarJidArray) {
                    const error = new Error("Anomaly getMediaPillarData");
                    throw (error);
                }
                //RQRAINB-6670 use the 1st one for backward compatibility
                const mediaPillarJid = mediaPillarJidArray[0];
                this.mediaPillarContact = this.createMediaPillarContact(mediaPillarJid); // 1PCC CPaaS
                this.mediaPillarConnection.mediaPillarJidArray = mediaPillarJidArray;
                this.logger.info("[WebrtcGatewayService] Pillar remoteExtension used : " + this.mediaPillarConnection.remoteExtension);
                if (this.mediaPillarConnection.remoteExtension !== "") {
                    //RQRAINB-6670++
                    this.mediaPillarConnection.mediaPillarJidArray.forEach((MPJid) => {
                        this.logger.info("[WebrtcGatewayService] MPJid: " + MPJid);
                        this.mediaPillarConnection.mediaPillarStatesPool[MPJid] = { mediaPillarConfigured: true, mediaPillarAlive: false };
                    });
                    this.setMediaPillarConfigured(this.isMediaPillarPoolConfigured(null));//at least one MP configured
                    //RQRAINB-6670--
                    //find a best solution
                    if (this.telephonyService?.started) {
                        this.telephonyService.getNomadicStatus();
                    }
                }
                else {
                    this.logger.warn("[WebrtcGatewayService] no remoteExtention available ");
                    this.setMediaPillarConfigured(false);
                }

                if (this.mediaPillarConfigured) {
                    try {
                        await this.registerMediaPillarPool();
                    }
                    finally {
                        if (this.mediaPillarAlive) {
                            this.logger.info("[WebrtcGatewayService] Pseudo media pillar register OK with extension " + this.mediaPillarConnection.remoteExtension);
                            this.eventService.publish("ON_WEBRTCGATEWAY_STATE_CHG", this.mediaPillarAlive);
                        } else {
                            this.logger.info("[WebrtcGatewayService] Pseudo media pillar register KO");
                            this.logger.info("[WebrtcGatewayService] Initial polling activated on starting issue");
                            this.mediaPillarKeepAlivePolling("START", TO_PILLAR_POLLING_MIN);
                            this.eventService.publish("ON_WEBRTCGATEWAY_STATE_CHG", this.mediaPillarAlive);
                        }
                    }
                }

                this.xmppConnectionSubscription = this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
                    if (event.name === 'ON_XMPP_DISCONNECTED') {
                        this.logger.info("[WebrtcGatewayService] onConnectionStateChangeEvent : disconnected then MP keepalive suspended");
                        this.mediaPillarKeepAliveSuspend = true;
                    }
                    else if (event.name === 'ON_XMPP_CONNECTED') {
                        this.logger.info("[WebrtcGatewayService] onConnectionStateChangeEvent : connected then keepalive as necessary");
                        this.mediaPillarKeepAliveSuspend = false;

                        // Now the xmpp connection is re-establshed, we must restart the mp polling which was suspended
                        if (this.isMediaPillarUserSelected()) {
                            // use a short delay before mp reconnection
                            this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_SHORT;
                            this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                        }
                    }
                });

                if (this.webrtcP2PService) {
                    this.subscriptions.push((this.webrtcP2PService.subscribe((event: RBEvent) => {
                        try {
                            if (event?.name === WebrtcP2PServiceEvents.RAINBOW_ON_MEDIA_PILLAR_CALL_UPDATED_EVENT) {
                                this.onCallEvent(event, event.data.call, null);
                            }
                        }
                        catch {
                            //do nothing
                        }
                    })));
                }

                // Subscribe to call events
                this.listeners.push(this.eventService.subscribe("ON_CALL_UPDATED_EVENT", (event, call, infoEvt) => { this.onCallEvent(event, call, infoEvt); }));

                // Subscribe to call remotly controlled event (controled by another ressource)
                this.listeners.push(this.eventService.subscribe("ON_MEDIAPILLAR_CALL_REMOTE_CTRL_EVENT", (event, call) => { this.onRemoteCtrlCall(event, call); }));

                //RQRAINB-9057
                this.listeners.push(this.eventService.subscribe("ON_MEDIAPILLAR_CALL_REMOTE_RESERVED_EVENT", (event, call) => { this.onRemoteReservedCall(event, call); }));

                // Subscribe to call reject
                this.listeners.push(this.eventService.subscribe("ON_MEDIAPILLAR_CALL_REJECT_EVENT", (__event, __webrtcCall) => {
                    let call = null;
                    for (const key in this.telephonyService.calls) {
                        if (this.telephonyService.calls.hasOwnProperty(key)) {
                            //if (this.telephonyService.calls[key].getMediaPillarCall().webrtcCallRef.id === webrtcCall.id &&
                            if (this.telephonyService.calls[key].callStatus === CallStatus.RINGING_INCOMMING) {
                                call = this.telephonyService.calls[key];
                                break;
                            }

                        }
                    }
                    this.logger.info("[WebrtcGatewayService] on_mediapillar_call_reject_event");

                    if (call) {
                        this.telephonyService.releaseCall(call);
                        call.setStatus(CallStatus.ENDED);
                        this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                    }
                }));

                // Subscribe to callrefs' replacement requests (asked by hybrid handler for OXO)
                this.listeners.push(this.eventService.subscribe("ON_REPLACE_CALLREFS_REQUEST", (__event, newCall: CallRB, call: CallRB) => {
                    this.logger.info(`[WebrtcGatewayService] ON_REPLACE_CALLREFS_REQUEST newCall ${newCall.toString()} call ${call.toString()}`);
                    this.replaceCallRefs(newCall, call);
                }));

                // Subscribe to call remotly controlled event (controled by another ressource)
                this.listeners.push(this.eventService.subscribe("ON_ANSWER_CALL_REQUEST", (__event, call: CallRB) => {
                    this.logger.info(`[WebrtcGatewayService] ON_ANSWER_CALL_REQUEST call ${call.toString()}`);
                    this.answerCall(call);
                }));

                this.logger.info("[WebrtcGatewayService] === STARTED ===");
            } catch (error) {
                this.started = false;
                this.mediaPillarConnection.mediaPillarJidArray = [];
                this.mediaPillarConnection.rainbowPhoneNumber = "";
                this.mediaPillarConnection.remoteExtension = "";
                this.setMediaPillarConfigured(false);
                this.mediaPillarAlive = false;
                this.mediaPillarConnection.mediaPillarStatesPool = {};//RQRAINB-6670
                this.eventService.publish("ON_WEBRTCGATEWAY_STATE_CHG", this.mediaPillarAlive);
                this.logger.info("[WebrtcGatewayService] === STARTING FAILURE === " + error.message);

                // try to reinit again in 30 sec
                this.startRetryTimer = setTimeout(() => {
                    this.logger.debug("[WebrtcGatewayService] -- try to reinit after 30sec timeout");
                    this.start();
                }, 30000);
            }
        }
        else {
            this.logger.info("[WebrtcGatewayService] === NO WEBRTCGATEWAY ENVOLVED === ");
        }

        this.subscriptions.push(this.userService.subscribe((event: any) => {
            if (event.name === UserServiceEvents.ON_USER_CHANGED) {
                if (this.contactService.isUserContact(event.data.user)) {
                    // reinitialize service data after user telephony config changed (by admin...)
                    if (this.telConfigChanged()) {
                        this.logger.info("[WebrtcGatewayService] User config changed => reinitialize service");
                        this.stop();
                        this.start();
                    }
                }
            }
        }));
    }

    private telConfigChanged(): boolean {
        return this.contactService.userContact?.systemId !== this.systemId
            || this.contactService.userContact?.displayName !== this.displayName
            || this.contactService.userContact?.oxeBusyCapability !== this.oxeBusyCapability
            || this.contactService.userContact?.systDeviceName !== this.systDeviceName;
    }

    public stop(): void {
        this.logger.info("[WebrtcGatewayService] === STOPPING ===");
        this.started = false;
        this.setMediaPillarConfigured(false);
        this.mediaPillarAlive = false;
        this.mediaPillarKeepAlivePolling("STOP", 0);
        if (this.startRetryTimer) {
            clearTimeout(this.startRetryTimer);
            this.startRetryTimer = null;
        }

        // If previous service fails to start, no object and stop services is called
        if (this.mediaPillarConnection) {
            this.mediaPillarConnection.mediaPillarJidArray = [];
            this.mediaPillarConnection.rainbowPhoneNumber = "";
            this.mediaPillarConnection.remoteExtension = "";
            this.mediaPillarConnection.mediaPillarStatesPool = {};//RQRAINB-6670
        }

        if (this.mediaPillarCallContext) {
            this.releaseMediaPillarCallContext();
            this.mediaPillarCallContext.callState = MPCallState.Unknow;
        }
        this.mediaPillarCallContexts.forEach((context) => { this.releaseMediaPillarCallContext(context); context.callState = MPCallState.Unknow; });
        this.mediaPillarCallContexts = [];//empty contexts table
        // Unregister event listeners
        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); }
        if (this.listeners) {
            this.listeners.forEach((unregisterListener) => { unregisterListener(); });
        }

        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
        this.subscriptions = [];

        this.logger.info("[WebrtcGatewayService] === STOPPED ===");
    }

    private setMediaPillarConfigured(isConfigured: boolean): void {
        this.mediaPillarConfigured = isConfigured;

        // notify configuration changes
        this.eventService.publish("ON_WEBRTCGATEWAY_CONFIG_CHANGE",
            this.mediaPillarConfigured,
            ((isConfigured && this.mediaPillarConnection) ? this.mediaPillarConnection.remoteExtension : "")
        );
    }

    // ************************************************************** //
    // ** CALL EVENT HANDLER                                        * //
    // ************************************************************** //

    private onCallEventPreProcessing(call: CallRB) {
        if (!call || call.callType !== CallType.WEBRTC || !this.isMediaPillarJid(call.fullJid)) { return; }

        this.logger.info("[WebrtcGatewayService] onCallEventPreProcessing for call WEBRTC MP " + call);

        try {
            // Send specific event for desktop (headset) to follow webrtc media acitivity
            this.eventService.publish('ON_MPMEDIA_CONVERSATION_CALL_UPDATED_EVENT', {
                call: call //the callId for webrtc part
            });

            // If the webrtc mediapillar call is active or released, let's notify the conversation for each attached phone call (in order to compute dtmf and mute buttons in telCallArea, which depend on webrtc call state)
            if (call.callStatus === CallStatus.ACTIVE || call.callStatus === CallStatus.ENDED) {
                if (call.mediaPillarCall) {

                    //we need to update all calls, so that the mute / dtmf buttons are updated correctly
                    this.conversationService.updateAllCallsCapabilities();

                    // update my contact capabilities to recompute telephony and webrtc contact capabilities (mainly used by makecall)
                    this.conversationService.computeCapabilitiesForMyContact();

                    const telCalls = call.mediaPillarCall.telephonyCallRefs;
                    (telCalls || []).forEach((telCall: CallRB) => {
                        if (telCall !== null && telCall.callType === CallType.PHONE) {
                            const conversation = this.conversationService.getConversationFromCache(telCall.conversationId);
                            conversation?.sendEvent(ConversationEvents.RAINBOW_CONVERSATION_CALL_UPDATE, { call });
                        }
                    });
                }

                //in case the webrtc part of the CSTA call is ended / active, check if we need to update the supervision state
                if (this.supervisionService) {
                    this.supervisionService.sendSupervisionUpdateMessage();
                }
            }

            // - We must not send the conversation_call event for a mediapilar webrtc incoming call, because it is generated by
            //    webrtcGatewayService if no csta event occurs on secondary device
            // - We send the conversation_call event in the other states (if the call displayed in incoming popup is the webrtc mp call only), so that the popup disappears if the caller hangs up
            if (call.callStatus !== CallStatus.RINGING_INCOMMING) {

                // this.computeCapabilitiesForMyContact(); /* mandatory for end of mediapilar call */

                if (call.isWebrtcMpIncomingCallPopup) {
                    // Send event
                    this.eventService.publish('ON_CONVERSATION_CALL_UPDATED_EVENT', {
                        conversation: null,
                        call: call
                    });

                    this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", call);
                }
            }
        } catch (error) {
            this.logger.error("[WebrtcGatewayService] onCallEventPreProcessing CATCH for WEBRTC MP call " + call + " error: " + error);
        }
    }

    /**
     * Method onCallEvent
     * @private
     * @memberof WebrtcGatewayService
     * Manage call events (but only if media pillar situation)
     * @param __event
     * @param call
     * @param infoEvt
     */
    private onCallEvent(__event, call: CallRB, infoEvt) {

        //CRRAINB-47528++
        // very specific hybrid MP situation when infra re-emit a webrtc ENDED evt for a call already terminated long time ago
        // we have to exclude this case (ignoring this evt) otherwise the evt will wrongly parasitize the only (Hybrid case)
        // current mediaPillar context already associated to a new ongoing other independant call
        if (!this.telephonyService.isSipWise() && this.isMediaPillarJid(call?.fullJid) && call?.callType === CallType.WEBRTC && call?.callStatus === CallStatus.ENDED) {
            if (this.mediaPillarCallContext.webrtcCallRef === null || this.mediaPillarCallContext.webrtcCallRef.id !== call.id) {
                this.logger.error("[WebrtcGatewayService] onCallEvent : for call = " + call.id + " / ghost ENDED evt filtered to not destroy the current context of another call. ");
                return;
            }
        }//CRRAINB-47528--

        // call update events refactoring : call here source code previously located in conversationservice, trigerred by CALL_UPDATE event on WEBRTC MP call
        this.onCallEventPreProcessing(call); //CRRAINB-40171

        if (this.isMediaPillarCallCase() || (this.isMediaPillarJid(call?.fullJid) && !this.telephonyService.isSipWise()) || (call?.getMixtWebTelSingleStepTrans())) { //CRRAINB-46519: fix of CRRAINB-34566 only if not sipwise
            //call.getMixtWebTelSingleStepTrans() = true => special singleteptransfer with webrtc&tel even when not in computer mode
            // cf CR25504-24670 & RQ6572

            if (!this.isMediaPillarCallCase()) {
                this.logger.debug("[WebrtcGatewayService] onCallEvent : isMediaPillarCallCase is false but call accepted");
            }

            const mediaPillarCallEventContext = this.getOrCreateMediaPIllarCallContext(call);
            if (!mediaPillarCallEventContext) {
                this.logger.info("[WebrtcGatewayService] onCallEvent : no mediaPillar Call Context found : Evt IGNORED");
                return;
            }

            this.SEM++;
            const oldState = mediaPillarCallEventContext.callState;
            //this.logger.info("[WebrtcGatewayService] onCallEvent IN(" + this.SEM + ") state = " + mediaPillarCallEventContext.callState);
            //this.logger.info("[WebrtcGatewayService] onCallEvent " + mediaPillarCallEventContext.callState + " --- " + call.callType.value + "(" + call.id + "," + call.callStatus.value + ")");

            //CRRAINB-5710++
            if (infoEvt && infoEvt !== null) {
                this.logger.info("[WebrtcGatewayService] onCallEvent tel infoEvt : Evt = " + infoEvt.actionElemName + " / cause = " + infoEvt.cause);
                //CRRAINB-5196++
                if (infoEvt.actionElemName === "updatecall" || infoEvt.actionElemName === "updateparty") {
                    mediaPillarCallEventContext.updateContactFlag = true;
                    this.logger.info("[WebrtcGatewayService] updateContactFlag = true");
                } //CRRAINB-5196--
            }//CRRAINB-5710--

            //debounce filter (to avoid double evt on same call id) //CRRAINB-5710=>do not debounce when updatecall
            if (call.connectionId === mediaPillarCallEventContext.previousCallConnectionId && call.callStatus === mediaPillarCallEventContext.previousCallStatus && !this.mediaPillarCallContext.updateContactFlag) {
                mediaPillarCallEventContext.previousCallConnectionId = call.connectionId;
                mediaPillarCallEventContext.previousCallStatus = call.callStatus;
                this.logger.info("[WebrtcGatewayService] onCallEvent | DEBOUNCE EVT");
                this.SEM--;
                this.logger.info("[WebrtcGatewayService] onCallEvent OUT(" + this.SEM + ")");
                return;
            }

            //filter phone call null (error case)
            if (call.callType === CallType.PHONE && call.id === null) {
                mediaPillarCallEventContext.previousCallConnectionId = call.connectionId;
                mediaPillarCallEventContext.previousCallStatus = call.callStatus;
                this.logger.info("[WebrtcGatewayService] onCallEvent | FILTER EVT null/error");
                this.SEM--;
                this.logger.info("[WebrtcGatewayService] onCallEvent OUT(" + this.SEM + ")");
                return;
            }

            mediaPillarCallEventContext.previousCallConnectionId = call.connectionId;
            mediaPillarCallEventContext.previousCallStatus = call.callStatus;

            this.evtQueue.push({ call, mediaPillarCallContext: mediaPillarCallEventContext }); //push in eventing queue
            if (this.evtQueue.length > 1) { //Meaning reentrancy case
                this.SEM--;
                this.logger.info("[WebrtcGatewayService] onCallEvent OUT(" + this.SEM + ") evt pushed = " + call.callStatus);
                return;
            }

            while (this.evtQueue.length) {
                try {
                    this.processEvt(this.evtQueue[0]);
                } catch (error) {
                    this.logger.error("[WebrtcGatewayService] onCallEvent queue processing | call : " + this.evtQueue[0].status + " Error : " + error);
                }
                this.evtQueue.splice(0, 1);
            }

            this.SEM--;

            let typeInfo: string = "Undefined";
            switch (call.callType) {
                case CallType.WEBRTC:
                    typeInfo = "Video";
                    break;
                case CallType.PHONE:
                    typeInfo = "Phone";
                    break;
                default: break;
            }

            this.logger.info("[WebrtcGatewayService] [TEL][GW] onCallEvt->" + typeInfo + "(" + call.id + "," + call.callStatus + ")" + "|old/new STA:" + oldState + "/" + this.mediaPillarCallContext.callState);
            //this.logger.info("[WebrtcGatewayService] onCallEvent OUT(" + this.SEM + ") state = " + this.mediaPillarCallContext.callState);
            if (this.SEM !== 0) {
                this.logger.error("[WebrtcGatewayService] onCallEvent OUT | Unmanaged Eventing Reentrancy Situation");
                this.resetEvtAutomaton();
                this.SEM = 0;
            }
        }
        else if (call && call.callType === CallType.WEBRTC && call.fullJid && this.isMediaPillarJid(this.xmppService.getBareJidFromJid(call.fullJid))) {
            switch (call.callStatus) {
                case CallStatus.ACTIVE: {
                    if (this.mediaPillarCallContext && this.mediaPillarCallContext.callState === MPCallState.CallActiveNoWebMedia) {
                        this.logger.info("[WebrtcGatewayService] case when WebrtcCall goes to Active -- (but MP is not connected)");
                        this.mediaPillarCallContext.callState = MPCallState.CallActive;

                        this.stopCallStateTimeOut();

                    }
                    break;
                }
                default: break;
            }
        }
    }

    /**
     * Method processEvt
     * @private
     * @param callEvent
     * @param {object} Call call evt
     * @memberof WebrtcGatewayService
     * Event processing
     */
    private processEvt(callEvent) {

        const call = callEvent.call as CallRB;
        this.mediaPillarCallContext = callEvent.mediaPillarCallContext;

        //this.logger.info("[WebrtcGatewayService] processEvt | process " + call.callType.value + "(" + call.id + "," + call.callStatus.value + ") in MPcontextCallstate = " + this.mediaPillarCallContext.callState + " CTX id " + this.mediaPillarCallContext.id);

        if (call.callType === CallType.PHONE) {
            // Tel case
            //From mediaPillar automat point of view only the 1st(resp last) tel call ref is relevant
            //All evt associated to 2nd or nth telcall situation are ignored except the releases active incomming queued
            //const telCallsNumber = this.telephonyService.getCalls().length;
            const evtToBeIgnored: boolean = (() => {
                let ignoreEvt = false;
                //NEW_MP1++
                if (call.callStatus === CallStatus.DIALING) { //RQRAINB-2660
                    call.isSecondary = true;
                }
                if (!call.isSecondary) {
                    this.logger.info("[WebrtcGatewayService] processEvt | NOT SECONDARY then ignore");
                    ignoreEvt = true;
                    return ignoreEvt;
                }
                //NEW_MP1--
                //NEW_MP2++
                if (call.callStatus === CallStatus.ENDED
                    || call.callStatus === CallStatus.RINGING_INCOMMING
                    || call.callStatus === CallStatus.QUEUED_INCOMMING
                    || call.callStatus === CallStatus.ACTIVE) {
                    this.addTelCallRefs(call); //add this telcallref in telephonyCallRefs
                    call.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context RQ6394
                    ignoreEvt = false; //when secondary never ignore those cases
                    return ignoreEvt;
                }

                if (this.mediaPillarCallContext.callState === MPCallState.RemoteControled) {
                    this.logger.info("[WebrtcGatewayService] processEvt |  RemoteControled state for call : " + call.id + " then ignore");
                    ignoreEvt = true; //ignore remote situation
                    return ignoreEvt;
                }
                //check if it is 2nd or nth call situation //NEW_MP1
                this.addTelCallRefs(call); //Before checking, add this telcallref in telephonyCallRefs
                if (this.mediaPillarCallContext.telephonyCallRefs.length > 1) {
                    this.logger.info("[WebrtcGatewayService] processEvt |  nthCall : " + this.mediaPillarCallContext.telephonyCallRefs.length + " then ignore");
                    ignoreEvt = true; //ignore 2nd/nth call
                    return ignoreEvt;
                }
                return ignoreEvt;
                //NEW_MP2--
            })();

            if (!evtToBeIgnored) { //process only the not ignored evt
                //this.addTelCallRefs(call); // systematically add this telcallref in telephonyCallRefs //NEW_MP2
                //this.logger.info("[WebrtcGatewayService] processEvt |  call(" + call.id + " / SEC " + call.isSecondary + ", " + call.callStatus.value + ") -> EVT PROCESSED");
                this.logger.info("[WebrtcGatewayService] processEvt |  telephonyCallRefs.length = " + this.mediaPillarCallContext.telephonyCallRefs.length);
                //this.logger.info("[WebrtcGatewayService] processEvt |  calls.length = " + telCallsNumber);
                switch (call.callStatus) {
                    case CallStatus.RINGING_INCOMMING: this.onTelCallIncomming(call); break;
                    case CallStatus.DIALING: this.onTelCallDialling(call); break;
                    case CallStatus.RINGING_OUTGOING: this.onTelCallOutgoing(call); break;
                    case CallStatus.ACTIVE: this.onTelCallActive(call); break;
                    case CallStatus.ENDED: this.onTelCallReleasing(call); break;
                    default: break;
                }
            }
            else { // ignored evt
                this.logger.info("[WebrtcGatewayService] processEvt |  call(" + call.id + " / SEC " + call.isSecondary + ", " + call.callStatus + ") -> EVT IGNORED");
                this.logger.info("[WebrtcGatewayService] processEvt |  telephonyCallRefs.length = " + this.mediaPillarCallContext.telephonyCallRefs.length);
                //this.logger.info("[WebrtcGatewayService] processEvt |  calls.length = " + telCallsNumber);
            }
        }
        // Webrtc case
        else if (this.isMediaPillarJid(this.xmppService.getBareJidFromJid(call.fullJid))) {
            switch (call.callStatus) {
                case CallStatus.RINGING_INCOMMING: this.onWebrtcCallIncomming(call); break;
                case CallStatus.CONNECTING: this.onWebrtcCallConnecting(call); break;
                case CallStatus.ACTIVE: this.onWebrtcCallActive(call); break;
                case CallStatus.ANSWERING: this.onWebrtcCallAnswering(call); break;
                case CallStatus.ENDED: this.onWebrtcCallReleasing(call); break;
                default: break;
            }
        }
        else {
            this.logger.info("[WebrtcGatewayService] processEvt | process call(" + call.id + "," + call.callStatus + ")" + " -> Not a webrtcgateway case");
        }
    }

    // *********************************** //
    // * MULTI CONTEXT MANAGEMENT        * //
    // *********************************** //

    public getOrCreateMediaPIllarCallContext(call: CallRB) {
        if (!this.telephonyService.isSipWise()) {
            // Hybrid telephony : OXE/OXE/THIRD_PARTY
            return this.mediaPillarCallContext;
        }

        //SIPWISE case
        if (call.callType === CallType.WEBRTC) {
            if (this.isMediaPillarJid(this.xmppService.getBareJidFromJid(call.fullJid))) {
                switch (call.callStatus) {
                    case CallStatus.RINGING_INCOMMING: return this.getOrCreateContextForWebrtcCallIncoming(call);
                    default: return this.getContextForWebrtcCallExisting(call);
                }
            }
        } else if (call.callType === CallType.PHONE) {
            return this.getOrCreateContextForTelCall(call);
        }
        return null;
    }

    public getOrCreateContextForTelCall(call: CallRB) {
        //this.logger.info("[WebrtcGatewayService] CTX getOrCreateContextForTelCall " + call);

        let foundContext = this.mediaPillarCallContexts.find((mediaPillarCallContext: MediaPillarCallContext) => {
            return mediaPillarCallContext.telephonyCallRefs
                && mediaPillarCallContext.telephonyCallRefs.length
                && mediaPillarCallContext.telephonyCallRefs[0].id === call.id;
        });
        if (foundContext) {
            //this.logger.info("[WebrtcGatewayService] CTX getOrCreateContextForTelCall " + call + " returns context id " + foundContext.id);
            return foundContext;
        }

        //if there is already a context waiting for a tel call (with the correct callId) : we use it,
        foundContext = this.mediaPillarCallContexts.find((mediaPillarCallContext: MediaPillarCallContext) => {
            return mediaPillarCallContext.callState === MPCallState.WaitTelephony
                && (!mediaPillarCallContext.webrtcCallRef.getAssocPhoneCallId()
                    || mediaPillarCallContext.webrtcCallRef.getAssocPhoneCallId().includes(call.id));
        });
        if (foundContext) {
            //this.logger.info("[WebrtcGatewayService] CTX getOrCreateContextForTelCall " + call + " returns context id " + foundContext.id);
            return foundContext;
        }
        return (call.callStatus === CallStatus.ENDED) ? null : this.createMediaPIllarCallContext();
    }

    public getOrCreateContextForWebrtcCallIncoming(call: CallRB) {
        //this.logger.info("[WebrtcGatewayService] CTX getOrCreateContextForWebrtcCallIncoming " + call);

        //if there is already a context waiting for an incoming webrtc call (with the correct phone callId) : we use it ,
        // otherwise we create a new context for this incoming call
        const foundContext = this.mediaPillarCallContexts.find((mediaPillarCallContext: MediaPillarCallContext) => {
            return mediaPillarCallContext.callState === MPCallState.WaitWebrtc
                && (!call.getAssocPhoneCallId()
                    || (mediaPillarCallContext.telephonyCallRefs
                        && mediaPillarCallContext.telephonyCallRefs.length
                        && call.getAssocPhoneCallId().includes(mediaPillarCallContext.telephonyCallRefs[0].id)));
        });
        if (foundContext) {
            //this.logger.info("[WebrtcGatewayService] CTX getOrCreateContextForWebrtcCallIncoming " + call + " returns context id " + foundContext.id);
            return foundContext;
        }
        return this.createMediaPIllarCallContext();
    }

    public getContextForWebrtcCallExisting(call: CallRB) {
        //this.logger.info("[WebrtcGatewayService] CTX getContextForWebrtcCallExisting " + call);

        const foundContext = this.mediaPillarCallContexts.find((mediaPillarCallContext: MediaPillarCallContext) => {
            return mediaPillarCallContext.webrtcCallRef && mediaPillarCallContext.webrtcCallRef.id === call.id;
        });

        //this.logger.info("[WebrtcGatewayService] CTX getContextForWebrtcCallExisting " + call + " returns context id " + (foundContext ? foundContext.id : " not found"));
        return foundContext;
    }

    public createMediaPIllarCallContext() {
        const newContext = {
            id: this.mediaPillarCallContextId++,
            callState: MPCallState.Free,
            callState_TO: null,
            waitTelephony_TO: null,
            isOutgoingCall3PCC: false,
            telephonyCallRefs: [],
            webrtcCallRef: null,
            previousCallConnectionId: null,
            previousCallStatus: null,
            updateContactFlag: false,
            onGoingCallMediaPillarJid: "", //RQRAINB-6670
            callOwnerForOxoMultiClientFlag: false, //RQ6394
            getMediaPillarJid: () => { return this.getOnGoingMediaPillarJid(); }//RQRAINB-6670
            //getMediaPillarJid: () => { return this.mediaPillarConnection.mediaPillarJid; }
        };
        this.mediaPillarCallContexts.push(newContext);

        //this.logger.info("[WebrtcGatewayService] CTX createMediaPIllarCallContext id " + newContext.id + " => nb contexts " + this.mediaPillarCallContexts.length);
        return newContext;
    }

    /**
     * Method releaseMediaPillarCallContext
     * @public
     * @memberof WebrtcGatewayService
     * Allow to free the media pillar call context (all field except mediaPillarJid)
     * but do not check the associated calls (tel and webrtc)
     * @param mediaPillarCallContext
     */
    public releaseMediaPillarCallContext(mediaPillarCallContext: MediaPillarCallContext = null) {
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }

        const formerCallContextState = mediaPillarCallContext.callState;//callSwitch adaptation
        mediaPillarCallContext.callState = MPCallState.Free;
        this.stopCallStateTimeOut(mediaPillarCallContext);
        this.stopWaitTelephonyTimeOut(mediaPillarCallContext);
        mediaPillarCallContext.isOutgoingCall3PCC = false;
        mediaPillarCallContext.updateContactFlag = false; //CCRAINB-4634
        if (mediaPillarCallContext.telephonyCallRefs.length > 0 && formerCallContextState !== MPCallState.RemoteControled) {
            this.logger.warn("[WebrtcGatewayService] releaseMediaPillarCallContext | reset context but telephonyCallRefs not empty !");
        }
        if (mediaPillarCallContext.webrtcCallRef !== null && formerCallContextState !== MPCallState.RemoteControled) {
            this.logger.warn("[WebrtcGatewayService] releaseMediaPillarCallContext | reset context but webrtcCallRef not null !");
        }
        mediaPillarCallContext.telephonyCallRefs = [];
        mediaPillarCallContext.webrtcCallRef = null;
        mediaPillarCallContext.previousCallConnectionId = null;
        mediaPillarCallContext.previousCallStatus = "";
        mediaPillarCallContext.onGoingCallMediaPillarJid = "";//RQRAINB-6670
        mediaPillarCallContext.callOwnerForOxoMultiClientFlag = false; //RQ6394

        if (this.mediaPillarCallContexts.length) { //callSwitch adaptation
            const index = this.mediaPillarCallContexts.indexOf(mediaPillarCallContext);
            if (index < 0 || index >= this.mediaPillarCallContexts.length) { //CRRAINB-43444
                //when not found index = -1 then the next splice will remove wrongly the last elt
                this.logger.error("[WebrtcGatewayService] Anomaly releaseMediaPillarCallContext id " + mediaPillarCallContext.id + " but context not found / remaining context = " + this.mediaPillarCallContexts.length);
                return;// in that case do nothing
            }
            this.mediaPillarCallContexts.splice(index, 1);
        }

        this.logger.info("[WebrtcGatewayService] releaseMediaPillarCallContext id " + mediaPillarCallContext.id + " => nb contexts " + this.mediaPillarCallContexts.length);

        this.logger.log("[WebrtcGatewayService] releaseMediaPillarCallContext | =====  mediaPillarCallContext RELEASED  ===== ");
    }

    /**
     * Method resetEvtAutomaton
     * @public
     * @memberof WebrtcGatewayService
     * Allow to reset the eventing automaton
     * linked to onCallEvent
     */
    public resetEvtAutomaton() {
        this.evtQueue = [];
    }

    /**
     * Method stopCallStateTimeOut
     * @private
     * @memberof WebrtcGatewayService
     * @param mediaPillarCallContext
     */
    private stopCallStateTimeOut(mediaPillarCallContext: MediaPillarCallContext = null) {
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }

        if (mediaPillarCallContext.callState_TO) { clearTimeout(mediaPillarCallContext.callState_TO); }
        mediaPillarCallContext.callState_TO = null;
    }

    /**
     * Method stopWaitTelephonyTimeOut
     * @private
     * @memberof WebrtcGatewayService
     * @param mediaPillarCallContext
     */
    private stopWaitTelephonyTimeOut(mediaPillarCallContext: MediaPillarCallContext = null) {
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }

        if (mediaPillarCallContext.waitTelephony_TO) { clearTimeout(mediaPillarCallContext.waitTelephony_TO); }
        mediaPillarCallContext.waitTelephony_TO = null;
    }

    /**
     * Method onRemoteCtrlCall
     * @private
     * @memberof WebrtcGatewayService
     * Traitment of multi ressources case when other ressource manage the call
     * @param __event
     * @param call
     */
    private onRemoteCtrlCall(__event, call: CallRB) {
        this.logger.info("[WebrtcGatewayService] onRemoteCtrlCall for call " + call.id + " in previous state: " + this.mediaPillarCallContext.callState + ": => Becomes REMOTECONTROLED");
        //get context
        let mediaPillarCallContext = call.getMediaPillarCall();
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }
        //update context state
        mediaPillarCallContext.callState = MPCallState.RemoteControled;
        this.stopCallStateTimeOut(mediaPillarCallContext);
        //CRRAINB-26056++
        if (this.telephonyService.isSipWise()) {
            if (mediaPillarCallContext.telephonyCallRefs[0]) {
                mediaPillarCallContext.telephonyCallRefs[0].remoteControled = true; //note : in siwise only 1 callRef used then store in array[0]
                this.telephonyService.clearCall(mediaPillarCallContext.telephonyCallRefs[0]); //and release call
            } else if (!this.telephonyService.isMediaPillarUserSelected()) {
                const allTelCalls: CallRB[] = this.telephonyService.getNotIdleCalls();
                const foundTelCall: CallRB = allTelCalls.find((telcall: CallRB) => {
                    return call.getAssocPhoneCallId()?.includes(telcall.id);
                });
                if (foundTelCall) {
                    this.logger.debug("[WebrtcGatewayService] onRemoteCtrlCall Not computer but associated telCall exists: " + foundTelCall + " => clear telCall");
                    foundTelCall.remoteControled = true;
                    this.telephonyService.clearCall(foundTelCall);
                } else {
                    this.logger.debug("[WebrtcGatewayService] onRemoteCtrlCall Not computer without existing tel Call => do nothing");
                }
            } else {
                this.telephonyService.remoteControlledPhoneCallIds.add(call.getAssocPhoneCallId());
                this.logger.debug("[WebrtcGatewayService] onRemoteCtrlCall remoteControlledPhoneCallId added, new set size: " + this.telephonyService.remoteControlledPhoneCallIds.size);
            }
        }//CRRAINB-26056--
    }

    /**
     * Method onRemoteReservedCall
     * @private
     * @memberof WebrtcGatewayService
     * Traitment of multi ressources case when other ressource reserve the call
     * @param __event
     * @param call
     */
    private onRemoteReservedCall(__event, call: CallRB) {
        this.logger.info("[WebrtcGatewayService] onRemoteReservedCall for call " + call.id + " in previous state: " + this.mediaPillarCallContext.callState + ": => Becomes REMOTECONTROLED");
        //get context
        let mediaPillarCallContext = call.getMediaPillarCall();
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }
        //update context state
        mediaPillarCallContext.callState = MPCallState.RemoteControled;
        this.stopCallStateTimeOut(mediaPillarCallContext);

        //RQRAINB-9057++
        if (mediaPillarCallContext.telephonyCallRefs.length >= 1) { //some Csta call ref
            mediaPillarCallContext.telephonyCallRefs.forEach((telCall) => {
                if (telCall.callStatus === CallStatus.RINGING_INCOMMING || telCall.callStatus === CallStatus.QUEUED_INCOMMING) {
                    telCall.remoteControled = true;
                    this.telephonyService.clearCall(telCall); //release call
                }
            });
        }
        //RQRAINB-9057--
    }

    /**
     * Method addTelCallRefs
     * @private
     * @memberof WebrtcGatewayService
     * add/push telcall ref in telephonyCallRefs if not already in
     * @param callRef
     */
    private addTelCallRefs(callRef) {
        //Warning basic implementation with indexOf doesn't suit!
        if (typeof (callRef) === "undefined" || callRef === null) {
            this.logger.warn("[WebrtcGatewayService] addTelCallRefs | ANOMALY callRef not valid");
            return;
        }
        let alreadyIn = false;
        for (let i = 0; i < this.mediaPillarCallContext.telephonyCallRefs.length; i++) {
            if (this.mediaPillarCallContext.telephonyCallRefs.length && (this.mediaPillarCallContext.telephonyCallRefs[i].id === callRef.id)) {
                alreadyIn = true;
            }
        }
        if (!alreadyIn) {
            this.mediaPillarCallContext.telephonyCallRefs.push(callRef);
        }
    }

    /**
     * Method removeTelCallRefs
     * @private
     * @memberof WebrtcGatewayService
     * remove telcall ref in telephonyCallRefs
     * @param callRef
     */
    private removeTelCallRefs(callRef) {
        //Warning basic implementation with indexOf doesn't suit!
        if (typeof (callRef) === "undefined" || callRef === null) {
            this.logger.warn("[WebrtcGatewayService] removeTelCallRefs | ANOMALY callRef not valid");
            return;
        }
        let i = 0;
        while (i < this.mediaPillarCallContext.telephonyCallRefs.length) {
            if (this.mediaPillarCallContext.telephonyCallRefs[i].id === callRef.id) {
                this.mediaPillarCallContext.telephonyCallRefs.splice(i, 1);
                if (i === 0 && this.mediaPillarCallContext.telephonyCallRefs.length) {
                    this.mediaPillarCallContext.telephonyCallRefs[0].setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
                    this.logger.log("[WebrtcGatewayService] removeTelCallRefs | master MP call becomes : " + this.mediaPillarCallContext.telephonyCallRefs[0].id);
                }
            }
            else {
                i++;
            }
        }
    }

    /**
     * Method putAsMasterTelCallRefs
     * @private
     * @memberof WebrtcGatewayService
     * The telephonyCallRefs[0] is intended to be the main telcall ref
     * usually the one which was handled as 1st to mediaPillaryse the call
     * @param callRef
     */
    private putAsMasterTelCallRefs(callRef) {
        //Warning basic implementation with indexOf doesn't suit!
        if (typeof (callRef) === "undefined" || callRef === null) {
            this.logger.warn("[WebrtcGatewayService] putAsMasterTelCallRefs | ANOMALY callRef not valid");
            return;
        }
        let i = -1;
        for (let j = 0; j < this.mediaPillarCallContext.telephonyCallRefs.length; j++) {
            if (this.mediaPillarCallContext.telephonyCallRefs[j].id === callRef.id) {
                i = j;
            }
        }
        this.logger.log("[WebrtcGatewayService] putAsMasterTelCallRefs | master MP call is : " + callRef.id);
        if (i === 0) { return; } //already done
        if (i !== -1) {
            const replace = this.mediaPillarCallContext.telephonyCallRefs[0]; //current [0] value
            this.mediaPillarCallContext.telephonyCallRefs[0] = callRef;
            this.mediaPillarCallContext.telephonyCallRefs[i] = replace;

            callRef.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
        }
        else {
            this.logger.warn("[WebrtcGatewayService] putAsMasterTelCallRefs | Anomaly call not in telephonyCallRefs");
        }
    }

    /**
     * Method isMasterTelCallRefs
     * @public
     * @memberof WebrtcGatewayService
     * return true if callRef is the master telcallref
     * @param callRef
     */
    public isMasterTelCallRefs(callRef): boolean {

        if (typeof (callRef) === "undefined" || callRef === null) {
            this.logger.warn("[WebrtcGatewayService] isMasterTelCallRefs | ANOMALY callRef not valid");
            return false;
        }

        if (this.mediaPillarCallContext.telephonyCallRefs.length === 0) {
            return false;
        }

        return (this.mediaPillarCallContext.telephonyCallRefs[0].id === callRef.id);
    }

    /**
     * Method isInTelCallRefs
     * @private
     * @memberof WebrtcGatewayService
     * @returns true if callRef is in telephonyCallRefs
     * @param callRef
     */
    private isInTelCallRefs(callRef): boolean {
        //return (this.mediaPillarCallContext.telephonyCallRefs.indexOf(callRef) !== -1);
        //Warning basic implementation with indexOf doesn't suit!
        if (typeof (callRef) === "undefined" || callRef === null) {
            this.logger.warn("[WebrtcGatewayService] isInTelCallRefs | ANOMALY callRef not valid");
            return false;
        }

        if (callRef.callType !== CallType.PHONE) {
            this.logger.warn("[WebrtcGatewayService] isInTelCallRefs | ANOMALY callRef not Tel PHONE");
            return false;
        }

        const mediaPillarCallContext = this.getMediaPillarCallContext(callRef);
        if (!mediaPillarCallContext) {
            this.logger.warn("[WebrtcGatewayService] isInTelCallRefs | ANOMALY mediaPillarCallContext not found");
            return false;
        }

        let i = -1;
        for (let j = 0; j < mediaPillarCallContext.telephonyCallRefs.length; j++) {
            if (mediaPillarCallContext.telephonyCallRefs[j].id === callRef.id) {
                i = j;
                break;
            }
        }
        return (i !== -1);
    }

    /**
     * Method replaceCallRefs
     * @private
     * @memberof WebrtcGatewayService
     * replace one callref with another
     * @param newCallRef
     * @param oldCallRef
     */
    public replaceCallRefs(newCallRef, oldCallRef) {
        if (typeof (newCallRef) === "undefined" || newCallRef === null || typeof (oldCallRef) === "undefined" || oldCallRef === null) {
            this.logger.warn("[WebrtcGatewayService] replaceCallRefs | ANOMALY callRef not valid");
            return;
        }
        //before to replace oldCallRef check if newCallRef not already existing
        let flagNewAlreadyExist = false;
        const flagNewLinked = newCallRef.isMediaPillarCall();
        const flagOldLinked = oldCallRef.isMediaPillarCall();
        if (this.isInTelCallRefs(newCallRef)) { //if existing remove it !!!!
            flagNewAlreadyExist = true;
            this.logger.warn("[WebrtcGatewayService] replaceCallRefs | ANOMALY newcallRef already existing");
            this.removeTelCallRefs(newCallRef);
        }

        let i = -1;
        //Warning basic implementation with indexOf doesn't suit!
        for (let j = 0; j < this.mediaPillarCallContext.telephonyCallRefs.length; j++) {
            if (this.mediaPillarCallContext.telephonyCallRefs[j].id === oldCallRef.id) {
                i = j;
            }
        }
        if (i !== -1) { //oldCallRef found then replace
            this.mediaPillarCallContext.telephonyCallRefs[i] = newCallRef;
            if (flagOldLinked) { //replicate the former link situation
                newCallRef.setMediaPillarCall(this.getMediaPillarCallContext()); //link the newcall to MP context
            }
            oldCallRef.setMediaPillarCall(null); //remove link from old callref to MP context
            if (i === 0) {
                this.logger.log("[WebrtcGatewayService] replaceCallRefs | master MP call becomes : " + this.mediaPillarCallContext.telephonyCallRefs[0].id);
            }
        } else {
            this.logger.warn("[WebrtcGatewayService] replaceCallRefs | ANOMALY oldCallRef not found");
            if (flagNewAlreadyExist) { //if newCallRef was removed restore it
                this.addTelCallRefs(newCallRef);
                if (flagNewLinked) { //was linked
                    newCallRef.setMediaPillarCall(this.getMediaPillarCallContext()); //link the newcall to MP context
                }
            }
        }
    }

    /**
     * Method automatonDefenseTimout
     * @private
     * @memberof WebrtcGatewayService
     * @param call
     * @param mediaPillarCallContext
     */
    private automatonDefenseTimout(call: CallRB, mediaPillarCallContext: MediaPillarCallContext = null) {
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }

        //Situation evaluation
        let actionLevel = 0; // default low level action
        if (call !== undefined && call !== null) {
            //telcall point of view
            if (call.callType === CallType.PHONE) {
                this.logger.log("[WebrtcGatewayService] automatonDefenseTimout | transition telcall call=" + call.id + " evt=" + call.callStatus + " then timout");
                switch (call.callStatus) {
                    case CallStatus.RINGING_OUTGOING:
                    case CallStatus.RINGING_INCOMMING:
                        break;
                    case CallStatus.ENDED: //CRRAINB-31191
                        if (mediaPillarCallContext.callState === MPCallState.TelCallReleasing) {
                            actionLevel = 3;
                        } else if (mediaPillarCallContext.callState !== MPCallState.CallActive
                            && mediaPillarCallContext.callState !== MPCallState.CallActiveNoWebMedia
                            && mediaPillarCallContext.callState !== MPCallState.CallOffer
                            && mediaPillarCallContext.callState !== MPCallState.CallOnGoing) {
                            actionLevel = 2;
                        }
                        break;
                    default:
                        if (mediaPillarCallContext.callState !== MPCallState.CallActive
                            && mediaPillarCallContext.callState !== MPCallState.CallActiveNoWebMedia
                            && mediaPillarCallContext.callState !== MPCallState.CallOffer
                            && mediaPillarCallContext.callState !== MPCallState.CallOnGoing) {
                            actionLevel = 2;
                        }
                        break;
                }
            }
            // Webrtc call point of view
            else if (this.isMediaPillarJid(this.xmppService.getBareJidFromJid(call.fullJid))) {
                this.logger.log("[WebrtcGatewayService] automatonDefenseTimout | transition webrtc " + call.callStatus + " then timout");
                switch (call.callStatus) {
                    case CallStatus.ACTIVE:
                        break;
                    default:
                        if (mediaPillarCallContext.callState !== MPCallState.CallActive
                            && mediaPillarCallContext.callState !== MPCallState.CallActiveNoWebMedia
                            && mediaPillarCallContext.callState !== MPCallState.CallOffer
                            && mediaPillarCallContext.callState !== MPCallState.CallOnGoing) {
                            actionLevel = 3; //CRRAINB-3500
                        }
                        break;
                }
            }
            else {
                this.logger.warn("[WebrtcGatewayService] automatonDefenseTimout | anomaly transition webrtc call but Not a webrtcgateway case");
                actionLevel = 2;
            }
        }
        else { // no call evt
            if (mediaPillarCallContext.callState !== MPCallState.Free //CRRAINB-43444
                && mediaPillarCallContext.callState !== MPCallState.CallActive
                && mediaPillarCallContext.callState !== MPCallState.CallActiveNoWebMedia
                && mediaPillarCallContext.callState !== MPCallState.RemoteControled
                && mediaPillarCallContext.callState !== MPCallState.CallOffer
                && mediaPillarCallContext.callState !== MPCallState.CallOnGoing) {
                this.logger.log("[WebrtcGatewayService] automatonDefenseTimout | anomaly timout in unexpected state");
                actionLevel = 2;
            }
        }

        //Actions
        if (actionLevel !== 0) { this.logger.warn("[WebrtcGatewayService] automatonDefenseTimout | actionlevel " + actionLevel); }
        switch (actionLevel) {
            case 0: // do nothing
                break;
            case 1: //Min keepalive polling
                this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN; //switch to MIn keepalive polling
                if (!this.mediaPillarAlive) {
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                }
                break;
            case 2: // free mediapillar call context + Min keepalive polling
                this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN;
                if (!this.mediaPillarAlive) {
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                }
                this.releaseMediaPillarCallContext(mediaPillarCallContext); // free mediapillar call context
                break;
            case 3: // release webrtc call + free mediapillar call context + Min keepalive polling
                this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN;
                if (!this.mediaPillarAlive) {
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                }
                if (this.telephonyService.isSipWise()) { //CRRAINB-37068
                    this.mediaPillarCallTerminator(mediaPillarCallContext);
                } else {
                    this.mediaPillarReleaseWebrtcCall(mediaPillarCallContext); //Free web call
                }
                this.releaseMediaPillarCallContext(mediaPillarCallContext); // free mediapillar call context
                break;
            default:
                this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN;
                if (!this.mediaPillarAlive) {
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                }
                this.mediaPillarCallTerminator(mediaPillarCallContext);
                break; // major or unknow anomaly => call terminator
        }
        //if MPCallState not FREE rearm Timout for survey
        if (mediaPillarCallContext.callState !== MPCallState.Free) {
            mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(null, mediaPillarCallContext); }, this.TO_transition);
        }
    }

    /** ======================================================================================== */
    /** ==================================== TELEPHONY EVENT =================================== */
    /** ======================================================================================== */

    /**
     * Method onTelCallIncomming
     * @private
     * @param {call} call [required] the call
     * @memberof WebrtcGatewayService
     */
    private onTelCallIncomming(call: CallRB) {
        //nth (multiple calls)
        if (this.mediaPillarCallContext.telephonyCallRefs.length > 1
            && this.mediaPillarCallContext.callState !== MPCallState.Free
            && this.mediaPillarCallContext.callState !== MPCallState.WaitTelephony
            && this.mediaPillarCallContext.callState !== MPCallState.WaitWebrtc
        ) {

            this.logger.info("[WebrtcGatewayService] onTelCallIncomming | nth calls BUT trig the GUI & link the call");
            call.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
            this.logger.info("[WebrtcGatewayService] onTelCallIncomming | ON_WEBRTC_GW_CALL_UPDATED_EVENT for call = " + call.id);

            if (call.autoOffHookResource) {
                // A call pickup has been engaged on our user, while there was already a incoming ringing call (hybrid usecase):
                // If it's engaged by our client, we must answer to this call (and not display the incoming call popup)
                if ((!this.isMediaPillarOutgoingCall() && this.mediaPillarCallContext.callState === MPCallState.CallOffer && this.mediaPillarCallContext.webrtcCallRef)) { //CR25504-24670
                    if (call.autoOffHookResource === this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid)) {
                        this.logger.info("[WebrtcGatewayService] onTelCallIncomming | blindPickup in progress : let's answer to call " + call.id);
                        call.setAutoOffHookInProgress(true);
                        this.answerCall(call);
                    } else {
                        this.logger.debug("[WebrtcGatewayService] onTelCallIncomming | blindPickup in progress on another client of our user: do nothing for call " + call.id);
                    }
                }
                return;
            }

            this.mediaPillarCallContext.updateContactFlag = true; //CCRAINB-4634
            this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", call);
            return;
        }
        //else
        this.logger.info("[WebrtcGatewayService] onTelCallIncomming | mediapilaryse as necessary for call " + call.id + " date " + format(new Date(), 'HH:mm:ss.SSS'));
        //mediaPillaryse the call and link it to the mediaPillar Call context
        switch (this.mediaPillaryseTheCall(call, null)) {
            case "Ok":
                //call.setMediaPillarCall(this.getMediaPillarCallContext());

                this.stopWaitTelephonyTimeOut();
                this.stopCallStateTimeOut();
                this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);

                if ((!this.isMediaPillarOutgoingCall() && this.mediaPillarCallContext.callState === MPCallState.CallOffer && this.mediaPillarCallContext.webrtcCallRef) || call.getMixtWebTelSingleStepTrans()) { //CR25504-24670
                    const isPopupWithWebrtcCall = this.mediaPillarCallContext.webrtcCallRef.isWebrtcMpIncomingCallPopup;
                    if (isPopupWithWebrtcCall) {
                        //this case should not happpen : if the popup with the webrtc call has been displayed , we should not receive any phone csta event
                        this.logger.info("[WebrtcGatewayService] onTelCallIncomming | Incoming tel phone while Popup displayed with Webrtc Call should happen in rare cases when csta event is very slow: use phone call info anyway for call " + call.id);
                        //update webrtc call with contact info from phone call, and post event to update incominng call popup
                        if (this.mediaPillarCallContext.webrtcCallRef.contact !== call.contact) {
                            this.mediaPillarCallContext.webrtcCallRef.contact = call.contact;
                            this.eventService.publish("ON_CONVERSATION_CALL_UPDATED_EVENT", {
                                conversation: null,
                                call: this.mediaPillarCallContext.webrtcCallRef
                            });
                            this.conversationService.updateAllCallsCapabilities();

                            this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", call);
                        }
                    }
                    if (call.autoOffHookResource) {
                        // A call pickup has been engaged on our user
                        // If it's engaged by our client, we must answer to this call (and not display the incoming call popup)
                        // If it's engaged by another client of our user, we must not answer to this call and not display the incoming call popup
                        //  (don't send ON_WEBRTC_GW_CALL_UPDATED_EVENT event)
                        if (call.autoOffHookResource === this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid)) {
                            this.logger.info("[WebrtcGatewayService] onTelCallIncomming | blindPickup in progress : let's answer to call " + call.id);
                            if (call.getInitCauseSipWiseData() === "singleStepTransfer") {
                                //in this case, we need to release the active webrtc call as this is the case of transfer PSTN call while active webrtc
                                this.logger.info("[WebrtcGatewayService] onTelCallIncomming | case of singleStepTransfer, release active webrtc call ");
                                const activeCall = this.webrtcP2PService.getWebrtcAudioCall();
                                if (activeCall) { this.webrtcP2PService.releaseCall(activeCall); }
                                //CR25504-24670 & RQ6572 ++
                                //maybe we also need to retrive the pending webrtc call not linked to the context if we are in office mode but have to use MP call
                                if (this.mediaPillarCallContext.callState === MPCallState.WaitWebrtc && !this.mediaPillarCallContext.webrtcCallRef && this.telephonyService.isSipWise()) {
                                    this.logger.info("[WebrtcGatewayService] onTelCallIncomming | case of singleStepTransfer, retrieve incoming MP webrtc media call ");
                                    const isAWebrtcCallOffer: CallRB = this.specialCaseNotComputerModeButMPCall(call);

                                    if (isAWebrtcCallOffer) { //MP webrtc associated call found
                                        this.logger.debug("[WebrtcGatewayService] onTelCallIncomming | blindPickup in progress for telcall:" + call.id + " and webCall: " + isAWebrtcCallOffer.id);
                                        this.answerCall(call);
                                    } else { // else do nothing and wait for next MP webrtc part
                                        this.logger.debug("[WebrtcGatewayService] onTelCallIncomming | blindPickup in progress for telcall:" + call.id + " but not yet webrtcCall");
                                    }
                                } else {
                                    this.answerCall(call);
                                }//CR25504-24670 & RQ6572 --
                            } else {
                                call.setAutoOffHookInProgress(true);
                                this.answerCall(call);
                            }
                        } else {
                            this.logger.debug("[WebrtcGatewayService] onTelCallIncomming | blindPickup in progress on another client of our user: do nothing for call " + call.id);
                        }
                    } else {
                        for (let i = 0; i < this.mediaPillarCallContext.telephonyCallRefs.length; i++) {
                            if ((!isPopupWithWebrtcCall && (this.mediaPillarCallContext.telephonyCallRefs[i].callStatus === CallStatus.RINGING_INCOMMING
                                || this.mediaPillarCallContext.telephonyCallRefs[i].callStatus === CallStatus.QUEUED_INCOMMING))
                                || isPopupWithWebrtcCall && this.mediaPillarCallContext.telephonyCallRefs[i].callStatus === CallStatus.QUEUED_INCOMMING
                            ) {
                                this.logger.info("[WebrtcGatewayService] onTelCallIncomming | ON_WEBRTC_GW_CALL_UPDATED_EVENT for call = " + call.id);
                                //this.mediaPillarCallContext.updateContactFlag = true; //  (CCRAINB-4634) -> CRRAINB-23654
                                this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", this.mediaPillarCallContext.telephonyCallRefs[i]);
                            }
                        }
                    }
                }
                break;
            case "Update": {
                this.stopCallStateTimeOut(); //CRRAINB-6167
                const pickupInProgress = call.getAutoOffHookInProgress();
                if (!this.isMediaPillarOutgoingCall() && !pickupInProgress && this.mediaPillarCallContext.webrtcCallRef
                    && (call.callStatus === CallStatus.RINGING_INCOMMING || call.callStatus === CallStatus.QUEUED_INCOMMING)) {
                    this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", call);
                    this.logger.info("[WebrtcGatewayService] onTelCallIncomming | update call");
                } else {
                    this.logger.info("[WebrtcGatewayService] onTelCallIncomming | ignore update call");
                }
                break;
            }
            case "Ignore":
                this.logger.info("[WebrtcGatewayService] onTelCallIncomming | ignore evt");
                break;
            default:
                this.logger.error("[WebrtcGatewayService] onTelCallIncomming | media Pillar call anomaly " + status);
                //this.mediaPillarCallTerminator(); //CRRAINB-5196
                break;
        }
    }

    /**
     * Method onTelCallDialling
     * @private
     * @param {call} call [required] the tel call
     * @memberof WebrtcGatewayService
     */
    private onTelCallDialling(call: CallRB) {
        //CRRAINB-15526++
        this.mediaPillarCallContext.isOutgoingCall3PCC = (call.isOwner || call.isMPAudioOwner); //check if outgoing 3PCC call //CRRAINB-15526 using isOwner flag
        this.logger.info("[WebrtcGatewayService] onTelCallDialling | isOwner " + call.isOwner + " isMPAudioOwner " + call.isMPAudioOwner + " => outgoing3PCC " + this.mediaPillarCallContext.isOutgoingCall3PCC);

        if (this.mediaPillarCallContext.isOutgoingCall3PCC) { //dialing state but I'm not caller/owner => other client (mobile...)
            if (this.mediaPillarCallContext.callState === MPCallState.Free
                || this.mediaPillarCallContext.callState === MPCallState.WaitTelephony) { //check if mediapillar context is ready for the incoming 3PCC call

                this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);
                //mediaPillaryse the call and link it to the mediaPillar Call context
                switch (this.mediaPillaryseTheCall(call, null)) {
                    case "Ok":

                        if (this.mediaPillarCallContext.webrtcCallRef && this.mediaPillarCallContext.callState === <MPCallState>MPCallState.CallOffer) { // web part ready
                            //then auto answer to the webrtc part
                            this.webrtcP2PService.answerCall(this.mediaPillarCallContext.webrtcCallRef, "audio");
                            call.isMPAudioOwner = true;
                            this.logger.info("[WebrtcGatewayService] onTelCallDialling | outgoing3PCC then send webrtc proceed");
                        }
                        this.stopCallStateTimeOut();
                        this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);
                        break;
                    case "Ignore":
                    case "Update":
                        this.logger.info("[WebrtcGatewayService] onTelCallDialling | ignore evt");
                        break;
                    default:
                        this.logger.error("[WebrtcGatewayService] onTelCallDialling | media Pillar call anomaly " + status);
                        this.mediaPillarCallTerminator();
                        break;
                }
            }
            else {
                if (this.mediaPillarCallContext.callState === MPCallState.CallOffer || this.mediaPillarCallContext.callState === MPCallState.CallOnGoing) { //CRRAINB-3952
                    this.logger.warn("[WebrtcGatewayService] onTelCallDialling | media Pillar call anomaly, on state : " + this.mediaPillarCallContext.callState);
                }
                this.logger.info("[WebrtcGatewayService] onTelCallDialling | ignored , on state : " + this.mediaPillarCallContext.callState);
                // else {
                //     this.logger.warn("[WebrtcGatewayService] onTelCallDialling | media Pillar call anomaly, MP context not compatible");
                //     this.mediaPillarCallTerminator();
                // }
            }
        } else {
            this.stopCallStateTimeOut();
            this.logger.info("[WebrtcGatewayService] onTelCallDialling but not call owner (=>other client) | ignore evt on state : " + this.mediaPillarCallContext.callState);
        }//CRRAINB-15526--
    }

    /**
     * Method onTelCallOutgoing
     * @private
     * @param {call} call [required] the tel call
     * @memberof WebrtcGatewayService
     */
    private onTelCallOutgoing(call) {
        //check if it is not a 1st outgoing call
        //or not a sipwise blind transfer case (CR20600) because when sipwise user is victime of a blind transfer he goes from active to outgoing state
        if (!this.isMediaPillarOutgoingCall() && this.mediaPillarCallContext.callState !== MPCallState.CallActive) {
            this.logger.warn("[WebrtcGatewayService] onTelCallOutgoing | media Pillar, not a 1st 3PCC outgoing call then ignore & release MP CallContext");
            this.releaseMediaPillarCallContext();
        }
        else {
            this.stopCallStateTimeOut();
        }
    }

    /**
     * Method onTelCallActive
     * @private
     * @param {call} call [required] the tel call
     * @memberof WebrtcGatewayService
     */
    private async onTelCallActive(call: CallRB): Promise<void> {

        switch (this.mediaPillarCallContext.callState) {
            case MPCallState.CallOnGoing: //normal case
                this.stopCallStateTimeOut();
                call.setMediaPillarCall(this.getMediaPillarCallContext()); //useful when the csta call appears directly with established state (parallel group member for example)
                this.mediaPillarCallContext.callState = MPCallState.CallActive;
                break;
            case MPCallState.TelCallReleasing: //probably a CONF or transfer case or other tel complexe case
                this.logger.warn("[WebrtcGatewayService] onTelCallActive | media Pillar recv tel active on state = " + this.mediaPillarCallContext.callState);
                this.logger.info("[WebrtcGatewayService] onTelCallActive | media Pillar , RE-ACTIVATION");
                this.stopCallStateTimeOut();
                call.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context  : MP_BIG_BUG
                //CRRAINB-25490++
                if (!this.telephonyService.oxoMultiCallMode()) { //"normal case"
                    this.mediaPillarCallContext.callState = MPCallState.CallActive;
                } else { //oxo multicall case : this.telephonyService.oxoMultiCallMode() = true
                    this.logger.info("[WebrtcGatewayService] onTelCallActive -- OXO specific mng for call when re-activation : " + call.id);
                    const mediaPillarCall = call.getMediaPillarCall();
                    if (mediaPillarCall?.webrtcCallRef?.callStatus === CallStatus.ACTIVE) {
                        //CRRAINB-38141 if webrtc still in active state nothing to do except to pass in global active state (do not re-arm automatonDefenseTimout)
                        this.mediaPillarCallContext.callState = MPCallState.CallActive;
                    }
                    else
                        if (mediaPillarCall?.webrtcCallRef?.callStatus === CallStatus.RINGING_INCOMMING && this.mediaPillarCallContext.callOwnerForOxoMultiClientFlag) {
                            call.noIncomingPopup = true;
                            await this.actionOnActiveCallIfNecessary(call);
                            if (mediaPillarCall?.webrtcCallRef?.callStatus === CallStatus.RINGING_INCOMMING) {
                                // answer to the webrtc incoming call
                                this.logger.info("[WebrtcGatewayService] onTelCallActive -- OXO specific mng | send webrtc proceed for call : " + call.id);
                                mediaPillarCall.callState = MPCallState.CallOnGoing;//waiting Active state for webrtc media
                                this.webrtcP2PService.answerCall(mediaPillarCall.webrtcCallRef, 'audio');
                                call.isMPAudioOwner = true;
                            } else {
                                this.logger.info("[WebrtcGatewayService] onTelCallActive | oxo case when re-activation  because not webrtc in incoming state");
                            }
                            this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);//CRRAINB-38141
                        }
                        else {
                            this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);
                            this.logger.info("[WebrtcGatewayService] onTelCallActive | oxo case when re-activation not possible because of state ano");//CRRAINB-38141
                        }
                }//CRRAINB-25490--
                break;
            case MPCallState.CallActive:
                this.logger.info("[WebrtcGatewayService] onTelCallActive | media Pillar , ignore tel active on state CallActive ");
                break;
            case MPCallState.WebCallReleasing:
                this.logger.info("[WebrtcGatewayService] onTelCallActive | media Pillar recv tel active on state WebCallReleasing then releaseMediaPillarCallContext");
                this.releaseMediaPillarCallContext(); // probably call taken by multidevice side
                break;
            case MPCallState.Free:
                this.logger.info("[WebrtcGatewayService] onTelCallActive | media Pillar recv tel active on state = 1 ; probably call taken on deskphone ; ignore active & release call context ");
                this.releaseMediaPillarCallContext();
                break;
            case MPCallState.CallOffer: //RQ6394++
                if (this.telephonyService.oxoMultiCallMode()) {
                    this.logger.info("[WebrtcGatewayService] onTelCallActive -- OXO specific mng for call : " + call.id);
                    this.stopCallStateTimeOut();
                    const mediaPillarCall = call.getMediaPillarCall();
                    if (mediaPillarCall && mediaPillarCall.webrtcCallRef && mediaPillarCall.webrtcCallRef.callStatus === CallStatus.RINGING_INCOMMING && this.mediaPillarCallContext.callOwnerForOxoMultiClientFlag) {
                        call.noIncomingPopup = true;
                        await this.actionOnActiveCallIfNecessary(call);
                        // answer to the webrtc incoming call
                        this.logger.info("[WebrtcGatewayService] onTelCallActive -- OXO specific mng | send webrtc proceed for call : " + call.id);
                        mediaPillarCall.callState = MPCallState.CallOnGoing;//waiting Active state for webrtc media
                        this.webrtcP2PService.answerCall(mediaPillarCall.webrtcCallRef, 'audio');
                        call.isMPAudioOwner = true;
                    } else {
                        this.logger.info("[WebrtcGatewayService] onTelCallActive | oxo case not possible to answer webrtc part");
                    }
                    this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);
                    break;
                }//else same as default => anomaly
                //RQ6394--

            // falls through
            default:
                this.logger.info("[WebrtcGatewayService] onTelCallActive | Anomaly media Pillar recv tel active on state = " + this.mediaPillarCallContext.callState);
                break;
        }
    }

    /**
     * Method onTelCallReleasing
     * @private
     * @param {call} call [required] the tel call
     * @memberof WebrtcGatewayService
     */
    private onTelCallReleasing(call) {

        const callsNumber = this.telephonyService.getCalls().length;
        if (this.mediaPillarCallContext.telephonyCallRefs.length > 1) { //CCRAINB-3750
            call.setMediaPillarCall(null); //remove link from call to MP context
            this.removeTelCallRefs(call); //remove link from MP context to call
            this.logger.info("[WebrtcGatewayService] onTelCallReleasing -- " + call.id + " -- postpone mediaPillarCallContext release ;  remaining calls = " + this.mediaPillarCallContext.telephonyCallRefs.length);
            return;
        }

        if (this.isMediaPillarReleasableCall()) { //check if MP call state is compatible
            call.setMediaPillarCall(null); //remove link from call to MP context
            this.removeTelCallRefs(call); //remove link from MP context to call
            if (this.mediaPillarCallContext.webrtcCallRef && this.mediaPillarCallContext.callState !== MPCallState.WebCallReleasing && this.mediaPillarCallContext.callState !== MPCallState.RemoteControled) {
                if (this.mediaPillarCallContext.callState !== MPCallState.TelCallReleasing) {
                    this.mediaPillarCallContext.callState = MPCallState.TelCallReleasing;
                    this.stopCallStateTimeOut();
                    this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition);
                    this.logger.info("[WebrtcGatewayService] onTelCallReleasing -- " + call.id + " -- wait web release: state = " + this.mediaPillarCallContext.callState);
                }
                else {
                    this.logger.info("[WebrtcGatewayService] onTelCallReleasing -- " + call.id + " -- ignore telrelease on state = " + this.mediaPillarCallContext.callState);
                }
                /* WARNING REENTRANCY PB on call of this.xxxx.releaseCall() */
                //this.webrtcP2PService.releaseCall(this.mediaPillarCallContext.webrtcCallRef); //CLAMERDE cas de reentrance!!!
            }
            else { //directly release MP call context exept if MPCallState.Free
                if (this.mediaPillarCallContext.callState !== MPCallState.Free) {
                    this.releaseMediaPillarCallContext(); //free mediapillar call context
                    this.logger.info("[WebrtcGatewayService] onTelCallReleasing -- " + call.id + " -- release MP context");
                }

            }
        }
        else {
            this.logger.warn("[WebrtcGatewayService] onTelCallReleasing | media Pillar call release anomaly on state = " + this.mediaPillarCallContext.callState);
            this.mediaPillarCallTerminator();
        }
    }

    /** ======================================================================================== */
    /** ====================================  WEBRTC EVENT  ==================================== */
    /** ======================================================================================== */

    /**
     * Method onWebrtcCallIncomming
     * @private
     * @param {call} call [required] the call
     * @memberof WebrtcGatewayService
     */
    private onWebrtcCallIncomming(call) {

        if (this.mediaPillarCallContext.webrtcCallRef) {
            this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming -- " + call.id + " --- ignored other incomming");
            return;
        }
        //RQRAINB-1548++
        if (this.mediaPillarCallContext.callState === MPCallState.WebCallReleasing && this.mediaPillarCallContext.telephonyCallRefs.length !== 0
            && this.mediaPillarCallContext.telephonyCallRefs[0].callStatus !== CallStatus.ENDED) {
            //webCall re-propose (following previous webref released) while tel call still engaged :
            //special OXE/OXO case when double call with second call releasing and then 1st call use new a webrtc media
            this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming -- " + call.id + " --- re propose during ongoing tel call");
            //CRRAINB-20444++
            if (this.mediaPillarCallContext.telephonyCallRefs[0].callStatus === CallStatus.QUEUED_INCOMMING
                || this.mediaPillarCallContext.telephonyCallRefs[0].callStatus === CallStatus.RINGING_INCOMMING) {
                //to manage specific CRRAINB-20444
                //in this re propose situation the ongoing former call is in queuedIncomming and have to be re-mediaPillarysed with the new
                this.mediaPillarCallContext.callState = MPCallState.WaitWebrtc;
                this.mediaPillarCallContext.telephonyCallRefs[0].setMediaPillarCall(this.getMediaPillarCallContext()); //link the tel call to MP context
            } else if (!this.telephonyService.isOxe || this.mediaPillarCallContext.telephonyCallRefs[0].callStatus === CallStatus.HOLD) { //to RQRAINB-1548 case
                //CRRAINB-49302 : In oxe case, limit the auto-answer mechanism for the case of RQRAINB-1548 & CRRAINB-20444 with the remaining call in hold
                this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming -- " + call.id + " --- auto-answer for the remaining 1st call");
                this.mediaPillarCallContext.webrtcCallRef = call;
                this.mediaPillarCallContext.webrtcCallRef.setMediaPillarCall(this.getMediaPillarCallContext());
                this.webrtcP2PService.answerCall(this.mediaPillarCallContext.webrtcCallRef, "audio");
                this.stopCallStateTimeOut();
                this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition, 1);
                this.mediaPillarCallContext.callState = MPCallState.CallOnGoing;
                return;
            }//CRRAINB-20444--
        }//RQRAINB-1548--

        if (this.mediaPillarConnection.mediaPillarJidArray.includes(call.fullJid)) { this.mediaPillarCallContext.onGoingCallMediaPillarJid = call.fullJid; } //RQRAINB-6670

        //mediaPillaryse the call and link it to the mediaPillar Call context
        switch (this.mediaPillaryseTheCall(null, call)) {
            case "Ok":
                this.stopCallStateTimeOut();
                if (this.isMediaPillarOutgoingCall()
                    || !(this.mediaPillarCallContext.callState === MPCallState.WaitTelephony && this.telephonyService.isOxe)) {
                    // Note : if we are in state WaiTelephony for an incoming call, and with pbx oxe only, we don't arm this timer because we will arm the little waitTelephony_TO below
                    //this.logger.debug("[WebrtcGatewayService] onWebrtcCallIncomming: arm callState_TO");
                    this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition, 1);
                }

                if (this.isMediaPillarOutgoingCall()) { //if outgoing 3PCC call
                    //then answer to the webrtc part
                    this.webrtcP2PService.answerCall(this.mediaPillarCallContext.webrtcCallRef, "audio");
                    const outgoingCall: CallRB = this.telephonyService.getOngoingOutgoingCall();
                    if (outgoingCall) { outgoingCall.isMPAudioOwner = true; }
                    this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming | outgoing3PCC then send webrtc proceed");
                }
                else { //if incomming call Synchronyse the GUI conversation mng (present the ringing tel call)
                    if (this.mediaPillarCallContext.callState === MPCallState.WaitTelephony && this.telephonyService.isOxe) {
                        // This behaviour is dedicated to OXE, because it's needed for the OXE parallel group telephony support
                        this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming | incoming webrtc WaitTelephony on Oxe : arm waitTelephony_TO ");
                        //start a short timer to wait for telephony
                        this.stopWaitTelephonyTimeOut();
                        this.mediaPillarCallContext.waitTelephony_TO = setTimeout(() => { this.waitTelephonyTimeout(call, this.mediaPillarCallContext); }, this.TO_waitTelephony, 1);
                    }
                    else if (this.mediaPillarCallContext.callState === MPCallState.CallOffer) {
                        this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming | incoming webrtc + telephony");
                        this.stopCallStateTimeOut();//RQ6394
                        for (let i = 0; i < this.mediaPillarCallContext.telephonyCallRefs.length; i++) {
                            const telephonyCall = this.mediaPillarCallContext.telephonyCallRefs[i];
                            if (telephonyCall.callStatus === CallStatus.RINGING_INCOMMING
                                || telephonyCall.callStatus === CallStatus.QUEUED_INCOMMING
                            ) {
                                if (telephonyCall.autoOffHookResource) {
                                    // A call pickup has been engaged on our user
                                    // If it's engaged by our client, we must answer to this call (and not display the incoming call popup)
                                    // If it's engaged by another client of our user, we must not answer to this call and not display the incoming call popup
                                    //  (don't send ON_CONVERSATION_CALL_UPDATED_EVENT event)

                                    if (telephonyCall.autoOffHookResource === this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid)) {
                                        this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming | blindPickup in progress : let's answer to call " + telephonyCall.id);
                                        telephonyCall.setAutoOffHookInProgress(true);
                                        this.answerCall(telephonyCall);
                                    } else {
                                        this.logger.debug("[WebrtcGatewayService] onWebrtcCallIncomming | blindPickup in progress on another client of our user: do nothing for call " + telephonyCall.id);
                                    }
                                } else {
                                    this.logger.info("[WebrtcGatewayService] onWebrtcCallIncomming | ON_CONVERSATION_CALL_UPDATED_EVENT for call = " + this.mediaPillarCallContext.telephonyCallRefs[i].id);
                                    this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", telephonyCall);//CRRAINB-26782 + CRRAINB-26704 now use ON_WEBRTC_GW_CALL_UPDATED_EVENT instead of ON_CONVERSATION_CALL_UPDATED_EVENT
                                }
                            }
                        }
                    }
                }
                break;
            case "Ignore": break;
            default:
                this.logger.error("[WebrtcGatewayService] onWebrtcCallIncomming | media Pillar call anomaly " + status);
                break;
        }
    }

    /**
     * Method onWebrtcCallConnecting
     * @private
     * @param {call} call [required] the webrtc call
     * @memberof WebrtcGatewayService
     */
    private onWebrtcCallConnecting(call: any) {
        if (this.mediaPillarCallContext.callState === MPCallState.CallActive) {
            this.logger.info("[WebrtcGatewayService] onWebrtcCallConnecting -- media Pillar call reconnecting attempts for call : " + call);
            this.mediaPillarCallContext.callState = MPCallState.CallActiveNoWebMedia;
            //CRRAINB-3500 //arm tempo
            this.stopCallStateTimeOut();
            this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.timeOutCallActiveNoWebMedia(this.mediaPillarCallContext); }, this.TO_NoWebMedia, 1);
            //CRRAINB-3500
            return;
        }
        if (this.mediaPillarCallContext.callState === MPCallState.TelCallReleasing) { //CRRAINB-6895
            this.logger.info("[WebrtcGatewayService] onWebrtcCallConnecting -- media Pillar webrtc call reconnecting while TelCallReleasing");
            this.mediaPillarCallContext.callState = MPCallState.CallActiveNoWebMedia;
            this.stopCallStateTimeOut();
            this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.timeOutCallActiveNoWebMedia(this.mediaPillarCallContext); }, this.TO_NoWebMediaNotel, 1);
            return;
        }
    }

    /**
     * Method onWebrtcCallActive
     * @private
     * @param {call} call [required] the webrtc call
     * @memberof WebrtcGatewayService
     */
    private onWebrtcCallActive(call: any) {
        if (this.mediaPillarCallContext.callState === MPCallState.CallActiveNoWebMedia) {
            this.logger.info("[WebrtcGatewayService] onWebrtcCallActive -- media Pillar call reconnected");
            this.mediaPillarCallContext.callState = MPCallState.CallActive;
            //CRRAINB-3500 //stop tempo
            this.stopCallStateTimeOut();
            //CRRAINB-3500
        } else { //RQ6394++
            if (this.telephonyService.oxoMultiCallMode() && this.mediaPillarCallContext.callState === MPCallState.CallOnGoing) { //specific oxo case
                if (this.mediaPillarCallContext.telephonyCallRefs[0].callStatus === CallStatus.ACTIVE) {
                    this.stopCallStateTimeOut();
                    this.mediaPillarCallContext.callState = MPCallState.CallActive;
                    this.logger.info("[WebrtcGatewayService] onWebrtcCallActive -- OXO specific mng | MP call becomes fully Active ");
                } else {
                    this.logger.info("[WebrtcGatewayService] onWebrtcCallActive -- OXO specific mng call State Anomaly");
                }
            }
        }//RQ6394--
    }

    /**
     * Method onWebrtcCallAnswering
     * @private
     * @param {call} call [required] the webrtc call
     * @memberof WebrtcGatewayService
     */
    private onWebrtcCallAnswering(call) {
        this.mediaPillarCallContext.callState = MPCallState.CallOnGoing;
        this.stopCallStateTimeOut();
        this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition, 1);
    }

    /**
     * Method onWebrtcCallReleasing
     * @private
     * @param {call} call [required] the webrtc call
     * @memberof WebrtcGatewayService
     */
    private onWebrtcCallReleasing(call) {
        if (this.isMediaPillarReleasableCall()) { //check if MP call state is compatible
            call.setMediaPillarCall(null); //remove link from call to MP context
            this.mediaPillarCallContext.webrtcCallRef = null; //remove link from MP context to call

            if ((this.mediaPillarCallContext.telephonyCallRefs.length !== 0) && this.mediaPillarCallContext.callState !== MPCallState.TelCallReleasing
                && this.mediaPillarCallContext.callState !== MPCallState.RemoteControled) { //RQRAINB-1548+
                if (this.mediaPillarCallContext.callState !== MPCallState.WebCallReleasing) {
                    this.mediaPillarCallContext.callState = MPCallState.WebCallReleasing;
                    this.stopCallStateTimeOut();
                    this.mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call); }, this.TO_transition, 1);
                    this.releasePbxCallIfPbxConnectionDown();
                    for (let i = 0; i < this.mediaPillarCallContext.telephonyCallRefs.length; i++) {
                        if (this.mediaPillarCallContext.telephonyCallRefs[i].callStatus === CallStatus.RINGING_INCOMMING
                            || this.mediaPillarCallContext.telephonyCallRefs[i].callStatus === CallStatus.QUEUED_INCOMMING
                        ) {
                            this.logger.info("[WebrtcGatewayService] onWebrtcCallReleasing | ON_CONVERSATION_CALL_UPDATED_EVENT for call = " + this.mediaPillarCallContext.telephonyCallRefs[i].id);
                            this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", this.mediaPillarCallContext.telephonyCallRefs[i]);
                            // use ON_WEBRTC_GW_CALL_UPDATED_EVENT instead of ON_CONVERSATION_CALL_UPDATED_EVENT (to prevent sending event with null conversation: this would disturb the desktop customization feature, which is listening this ON_CONVERSATION_CALL_UPDATED_EVENT event)
                        }
                    }
                    this.logger.info("[WebrtcGatewayService] onWebrtcCallReleasing -- " + call.id + " -- wait tel release: state = " + this.mediaPillarCallContext.callState);
                }
                else {
                    this.logger.info("[WebrtcGatewayService] onWebrtcCallReleasing -- " + call.id + " -- ignore webrelease on state = " + this.mediaPillarCallContext.callState);
                }
                /* WARNING REENTRANCY PB on call of this.webrtcP2PService.releaseCall() */
            }
            else { //directly release MP call context
                this.releaseMediaPillarCallContext(); //free mediapillar call context
                this.logger.info("[WebrtcGatewayService] onWebrtcCallReleasing -- " + call.id + " -- release MP context");
            }

            // the isOutgoingCall3PCC property must be reset when the webrtc call is released, except if there is only one DIALING phone call
            if (!(this.mediaPillarCallContext.telephonyCallRefs.length === 1 && this.mediaPillarCallContext.telephonyCallRefs[0].callStatus === CallStatus.DIALING)) {
                this.mediaPillarCallContext.isOutgoingCall3PCC = false;
            }
        }
        else {
            this.logger.warn("[WebrtcGatewayService] onWebrtcCallReleasing | media Pillar call release anomaly on state = " + this.mediaPillarCallContext.callState);
            this.mediaPillarCallTerminator();
        }
    }

    /**
     * Method waitTelephonyTimeout
     * @private
     * @memberof WebrtcGatewayService
     * No incoming telephony call received before timeout
     * We display an incoming call popup with the mediapillar webrtc call if no call exists on secondary device
     * @param call
     * @param mediaPillarCallContext
     */
    private async waitTelephonyTimeout(call: CallRB, mediaPillarCallContext: MediaPillarCallContext): Promise<void> {
        if (mediaPillarCallContext.callState === MPCallState.WaitTelephony) {
            this.logger.info("[WebrtcGatewayService] waitTelephonyTimeout date " + format(new Date(), 'HH:mm:ss.SSS') + " -- " + call.id + " -- state = " + mediaPillarCallContext.callState + " : perform snpashot on secondary device to check remote extension's csta state");

            // Check the telephony state of the secondary device:
            // If some call exists, it's a standard incoming call or outgoing call (made by the webclient), or it may be an outgoing call
            //   made by a mobile device : we must not display any incoming call popup
            // If no call is present on secondary device, it may be an incoming call on parallel group member for Oxe (which has no csta part):
            // in this case, we will display the incoming popup with the webrtc call
            try {
                const result: any = await this.telephonyService.snapshotDevice(true);
                const connections = result ? result.connections : null;

                if (mediaPillarCallContext.callState === MPCallState.WaitTelephony && (!connections || connections.length === 0)) {
                    // No csta connections on remote extension:
                    // it may be an incoming call on oxe parallel grp member
                    // Let's display the incoming call popup with webrtc call
                    this.logger.info("[WebrtcGatewayService] waitTelephonyTimeout date " + format(new Date(), 'HH:mm:ss.SSS') + " -- " + call.id + " -- state = " + mediaPillarCallContext.callState
                        + " => still waiting for phone call and no csta call: send event to open incoming call popup with webrtc call");

                    // send event to display the incoming call popup with webrtc call
                    call.isWebrtcMpIncomingCallPopup = true;

                    this.conversationService.updateCallCapabilities(call);

                    this.eventService.publish("ON_CONVERSATION_CALL_UPDATED_EVENT", {
                        conversation: null,
                        call: call
                    });

                    //we need to create conversation with the webrtc MP call so that the incoming notification is created;
                    this.eventService.publish("ON_WEBRTC_GW_CALL_UPDATED_EVENT", call);

                } else {
                    this.logger.info("[WebrtcGatewayService] waitTelephonyTimeout date " + format(new Date(), 'HH:mm:ss.SSS') + " -- " + call.id + " -- state = " + mediaPillarCallContext.callState
                        + " => no more waiting for phone call or existing csta call: don't display any incoming call popup");
                    //wa are still waiting for a csta phone event
                    this.stopCallStateTimeOut(mediaPillarCallContext);
                    mediaPillarCallContext.callState_TO = setTimeout(() => { this.automatonDefenseTimout(call, mediaPillarCallContext); }, this.TO_transition, 1);
                }
            }
            catch (error) {
                this.logger.error("[WebrtcGatewayService] waitTelephonyTimeout date " + format(new Date(), 'HH:mm:ss.SSS') + " -- " + call.id + " -- state = " + mediaPillarCallContext.callState + " => snpashotdevice error " + error);
            }
        } else {
            this.logger.info("[WebrtcGatewayService] waitTelephonyTimeout date " + format(new Date(), 'HH:mm:ss.SSS') + " -- " + call.id + " -- state = " + mediaPillarCallContext.callState + " => IGNORED");
        }
        mediaPillarCallContext.waitTelephony_TO = null;
    }

    /**
     * Method releasePbxCallIfPbxConnectionDown
     * @private
     * @memberof WebrtcGatewayService
     * release the active pbx call if the pbx connection is down: this is called when we receive the session termination on the mp webrtc call
     */
    private releasePbxCallIfPbxConnectionDown() {
        const activePbxCall = this.getActivePbxCall();
        if (activePbxCall && activePbxCall.pbxConnectionDown) {
            this.logger.info("[WebrtcGatewayService] releasePbxCallIfPbxConnectionDown | release call" + activePbxCall);
            this.telephonyService.releaseCall(activePbxCall); //remove pbx call
            activePbxCall.mediaPillarCall = null; //and the link from this pbx call to the mediapillar call context
        }
    }

    // ******************************************************************* //
    // ** ACTION FUNCTIONS                                               * //
    // ******************************************************************* //
    /**
     * Method mediaPillarMakeCall1PCC
     * @private
     * @param {number} phoneNumber [required] the number to join by mediaPillar
     * @param {contact} contact [optional]  the called contact if available
     * @returns {call} call : the 1PCC call object
     * @memberof WebrtcGatewayService
     * make a media pillar call 1PCC to the phoneNumber
     */
    public mediaPillarMakeCall1PCC(phoneNumber: any, contact: any): CallRB {
        this.logger.info("[WebrtcGatewayService] mediaPillarMakeCall1PCC");
        const mediaPillarJid = this.getDefaultMediaPillarJid();

        if (!this.mediaPillarConfigured || !this.mediaPillarAlive) {
            this.logger.info("[WebrtcGatewayService] mediaPillarMakeCall1PCC ANOMALY webrtcgateway not available");
            return null;
        }
        //we're making a webrtc call right now
        this.webrtcP2PService.makingCall = true;

        if (mediaPillarJid !== "") {
            const id = "web_" + crypto.randomUUID();

            if (!contact || contact === null) { //no contact provided then use predefined mediaPillarContact
                // 1PCC CPaaS ++
                //Create the call object
                const call = CallRB.create(CallStatus.DIALING, id, CallType.WEBRTC, this.mediaPillarContact);
                call.fullJid = mediaPillarJid;
                this.mediaPillarStartCall(call, phoneNumber, mediaPillarJid);
                return call;
                // 1PCC CPaaS --
            }

            //Create the call object
            const call = CallRB.create(CallStatus.DIALING, id, CallType.WEBRTC, contact);
            call.fullJid = mediaPillarJid;
            this.mediaPillarStartCall(call, phoneNumber, mediaPillarJid);
            return call;
        }
        this.logger.info("[WebrtcGatewayService] mediaPillarMakeCall ANOMALY no mediaPillarJid");
        return null;
    }
    //end mediaPillarMakeCall1PCC

    /**
     * Method mediaPillarReleaseCall1PCC
     * @private
     * @param {call} call [required] the 1PCC call to release
     * @memberof WebrtcGatewayService
     * release media pillar call 1PCC previously initiated
     */
    public mediaPillarReleaseCall1PCC(call) {
        this.logger.info("[WebrtcGatewayService] mediaPillarReleaseCall1PCC");
        if (!this.mediaPillarConfigured) {
            this.logger.info("[WebrtcGatewayService] mediaPillarReleaseCall1PCC ANOMALY webrtcgateway not available");
            return;
        }
        const foundCall = this.webrtcP2PService.calls[call.id];
        if (!foundCall || foundCall.callType !== CallType.WEBRTC) {
            this.logger.info("[WebrtcGatewayService] mediaPillarReleaseCall1PCC ANOMALY invalid call");
        } else {
            this.webrtcP2PService.terminateCall(foundCall);
        }
    }//end mediaPillarMakeCall1PCC

    /**
     * Method mediaPillarMakeCall
     * @private
     * @param {number} phoneNumber [required] the number to join by mediaPillar
     * @param contact
     * @param {contact} [optional]  the called contact if available
     * @memberof WebrtcGatewayService
     * make a media pillar call to the phoneNumber
     */
    public mediaPillarMakeCall(phoneNumber, contact) {
        this.logger.info("[WebrtcGatewayService] mediaPillarMakeCall");
        const mediaPillarJid = this.getDefaultMediaPillarJid();

        //DEMO MODE++
        if (phoneNumber.length > 6 && contact) {
            phoneNumber = contact.phonePbx;
        }
        //DEMO MODE--

        //we're making a webrtc call right now
        this.webrtcP2PService.makingCall = true;

        if (mediaPillarJid !== "") {
            //this.webrtcP2PService.makeCall(calledContact, "WebRTCAudio", mediaPillarJid, phoneNumber);
            const id = "web_" + crypto.randomUUID();

            if (!contact || contact === null) { //no contact provided then use predefined mediaPillarContact
                // 1PCC CPaaS ++
                //Create the call object
                const call = CallRB.create(CallStatus.DIALING, id, CallType.WEBRTC, this.mediaPillarContact);
                call.fullJid = mediaPillarJid;
                this.mediaPillarStartCall(call, phoneNumber, mediaPillarJid);
                // 1PCC CPaaS --
            }
            else {
                //Create the call object
                const call = CallRB.create(CallStatus.DIALING, id, CallType.WEBRTC, contact);
                call.fullJid = mediaPillarJid;
                this.mediaPillarStartCall(call, phoneNumber, mediaPillarJid);
            }
        }
        else {
            this.logger.info("[WebrtcGatewayService] mediaPillarMakeCall ANOMALY no mediaPillarJid");
        }
    }

    /**
     * Method mediaPillarStartCall
     * @private
     * @param {call} call [required] the call to abort
     * @param {number} phoneNumber [required] the number to join by mediaPillar
     * @param {mediaPillarJid} mediaPillarJid [required]
     * @memberof WebrtcGatewayService
     */
    private async mediaPillarStartCall(call, phoneNumber, mediaPillarJid): Promise<void> {
        this.logger.info("[WebrtcGatewayService] mediaPillarStartCall for phone number " + phoneNumber);
        try {
            await this.initiateMediaPillarCall(call);
            try {
                await this.registerOneMediaPillar(mediaPillarJid);
                this.logger.info("[WebrtcGatewayService ] mediaPillarStartCall for number " + phoneNumber);
                this.webrtcP2PService.makeJingleCall(call, mediaPillarJid, phoneNumber);

            }
            catch (error) {
                this.logger.info("[WebrtcGatewayService] mediaPillarStartCall ANOMALY registerOneMediaPillar then abort call");
                this.abortCall(call);
            }
        }
        catch (error) {
            this.logger.info("[WebrtcGatewayService] mediaPillarMakeCall ANOMALY initiateMediaPillarCall then abort call");
            this.abortCall(call);
        }
    }

    /**
     * Method abortCall
     * @private
     * @param {call} call [required] the call to abort
     * @memberof WebrtcGatewayService
     */
    private abortCall(call) {
        //free the mackingCall flag
        this.webrtcP2PService.makingCall = false;

        if (call) {
            this.webrtcP2PService.removeCallObject(call);
        }

        this.webrtcP2PService.resetToSafeState();
    }

    /**
     * Method initiateMediaPillarCall
     * @private
     * @memberof WebrtcGatewayService
     * inspired by webrtcP2PServiceEventHandler.prototype.sendProposition
     * @param call
     */
    private async initiateMediaPillarCall(call: CallRB): Promise<void> {

        const mediaToGet = ["audio"];
        try {
            this.logger.info("[WebrtcGatewayService] initiateMediaPillarCall");
            const stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

            this.webrtcP2PService.calls[call.id] = call;
            call.isInitiator = true;
            this.webrtcP2PService.pushInLocalStreams(stream, call.id);
            call.localMedia = call.localMedia | CallMedia.AUDIO; //replace setLocalTypeMedia(mediaToGet, call);

            // Update presence
            this.contactService.setBusyState("dnd", this.webrtcP2PService.calculatePresenceMessage(call));

            // Send event
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

        }
        catch (error) {
            this.logger.info("[WebrtcGatewayService]   | initate call failure : " + JSON.stringify({ error: error.message }));
            if (!(mediaToGet.indexOf("sharing") >= 0) && error.toString().indexOf("getBrowserMedia") !== -1) {
                this.webrtcP2PService.openErrorPopup();
            }
        }
    }

    /**
     * Method registerOneMediaPillar
     * @private
     * @memberof WebrtcGatewayService
     * @param MPJid
     */
    private async registerOneMediaPillar(MPJid: string): Promise<void> {

        if (!MPJid) {
            this.logger.debug("[WebrtcGatewayService] registerOneMediaPillar -- missing MPJid --");
            const error = new Error("registerOneMediaPillar -- missing MPJid --");
            throw error;
        }
        if (!this.mediaPillarConnection.mediaPillarJidArray.includes(MPJid)) {
            this.logger.debug("[WebrtcGatewayService] registerOneMediaPillar -- unknow MPJid --" + MPJid);
            const error = new Error("registerOneMediaPillar -- unknow MPJid --" + MPJid);
            throw error;
        }
        if (!this.mediaPillarConnection.mediaPillarStatesPool[MPJid].mediaPillarConfigured) {
            this.logger.debug("[WebrtcGatewayService] registerOneMediaPillar -- MPJid not configured --" + MPJid);
            this.mediaPillarConnection.mediaPillarStatesPool[MPJid].mediaPillarAlive = false;
            const error = new Error("registerOneMediaPillar -- MPJid not configured --" + MPJid);
            throw error;
        }

        const xmppIq = $iq({ from: this.myContact.fullJid, to: MPJid, type: "set" })
            .c("mediapillar", { xmlns: 'urn:xmpp:janus:1' })
            .c("register")
            .c("jidIm").t(this.contactService.userContact.jid).up()
            .c("jidTel").t(this.contactService.userContact.jidtel).up()
            .c('number').t(this.mediaPillarConnection.rainbowPhoneNumber).up()
            .c('displayName').t(this.myContact.displayName).up()
            .c('secret').t(this.mediaPillarConnection.rainbowPhoneNumber).up();

        try {
            await this.xmppService.sendIQ(xmppIq);
            this.mediaPillarConnection.mediaPillarStatesPool[MPJid].mediaPillarAlive = true;
        }
        catch (error) {
            this.mediaPillarConnection.mediaPillarStatesPool[MPJid].mediaPillarAlive = false;
            //if (this.isMediaPillarUserSelected()) {
            const log = (error?.message?.indexOf("<error") !== -1) ? error?.message.substring(error?.message?.indexOf("<error")) : error.message;
            this.logger.info("[WebrtcGatewayService] registerOneMediaPillar -- register failure -- for MPJid = " + MPJid + " // error : " + log);
            //}
            throw error;
        }
    }//end registerOneMediaPillar

    /**
     * Method registerMediaPillarPool
     * @private
     * @memberof WebrtcGatewayService
     */
    private async registerMediaPillarPool(): Promise<void> {
        //All the MP pool to register
        const registerPromises = [];
        this.mediaPillarConnection.mediaPillarJidArray.forEach((mediaPillarJid: string) => {
            registerPromises.push((async (): Promise<void> => {
                try {
                    await this.registerOneMediaPillar(mediaPillarJid);
                } catch (error) {
                    //implicite success: Do not throw error because others MP could be alive whatever result resolve, synthese done in upper level with all results
                }
            })());
        });
        // when all processed
        try {
            await Promise.all(registerPromises);
        } finally {
            this.mediaPillarAlive = this.isMediaPillarPoolAlive(null);
        }
    }//end registerMediaPillarPool

    /**
     * Method getMediaPillarData
     * @public
     * @returns {Promise<any>} the mediaPillarJid data associated to the system & user
     * @memberof WebrtcGatewayService
     */
    public async getMediaPillarData(): Promise<any> {
        if (!this.telephonyService.isSipWise()) { //not sipwise
            try {
                const url = this.portalURL + "/data";
                const headers = this.authService.getRequestHeader();
                const response: Response = await fetch(url, { method: 'GET', headers });
                if (!response.ok) {
                    throw response;
                }
                const responseBody: any = await response.json();
                const data: any = responseBody.data;
                this.logger.log("[WebrtcGatewayService] getMediaPillarData success");
                if (data) {
                    const prefix = data.prefix;
                    const rainbowPhoneNumber = data.rainbowPhoneNumber;
                    if (prefix && rainbowPhoneNumber) {
                        this.mediaPillarConnection.rainbowPhoneNumber = rainbowPhoneNumber;
                        this.mediaPillarConnection.remoteExtension = prefix + rainbowPhoneNumber;
                    }
                    //RQRAINB-6670++
                    let MediaPillarJidArray = [];
                    if (data.jid_im && Array.isArray(data.jid_im)) { //answer with multiple WG
                        MediaPillarJidArray = data.jid_im;
                        MediaPillarJidArray = MediaPillarJidArray.map((eltJid) => { if (eltJid.indexOf("/") === -1) { eltJid += "/mediapillar"; } return eltJid; });// Add MP ressource, if missing
                    } else { //answer with mono WG structure
                        let jid = data.jid_im;
                        if (jid && jid.indexOf("/") === -1) { jid += "/mediapillar"; }// Add MP ressource, if missing
                        MediaPillarJidArray[0] = jid;
                    }///RQRAINB-6670++
                    return (MediaPillarJidArray);
                }

                // Missing data in the response
                this.logger.error("[WebrtcGatewayService] getMediaPillarData success -- missing data");
                throw (new Error("[WebrtcGatewayService] getMediaPillarData -- missing data in response"));

            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getMediaPillarData');
                this.logger.error(`[WebrtcGatewayService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        } //sipwise case
        try {
            const url = this.telephonyService.telephonyServiceSipWise.getPortalURL() + "devices";
            const headers = this.authService.getRequestHeader();
            this.logger.info("[WebrtcGatewayService] getMediaPillarData sipwise device  GET " + url);
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const data: any = await response.json();
            this.logger.log("[WebrtcGatewayService] getMediaPillarData sipwise success/ status : " + response.statusText);
            const MediaPillarJidArray = [];//RQRAINB-6670
            if (data) {
                data.forEach((device) => {
                    if (device.type === "webrtc") {
                        this.mediaPillarConnection.rainbowPhoneNumber = device.deviceId;
                        this.mediaPillarConnection.remoteExtension = device.deviceId;
                        let jid = device.jid_wrg;
                        if (jid && jid.indexOf("/") === -1) { jid += "/mediapillar"; }// Add MP ressource, if missing
                        MediaPillarJidArray.push(jid);//RQRAINB-6670
                    }
                });
                if (MediaPillarJidArray[0] !== "") {
                    return (MediaPillarJidArray);
                }
                // Missing data in the response
                this.logger.error("[WebrtcGatewayService] getMediaPillarData sipwise success but missing jid_wrg");
                throw (new Error("[WebrtcGatewayService] getMediaPillarData sipwise -- missing jid_wrg in response"));

            } else {
                // Missing data in the response
                this.logger.error("[WebrtcGatewayService] getMediaPillarData sipwise success but missing data");
                throw (new Error("[WebrtcGatewayService] getMediaPillarData sipwise -- missing data in response"));
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getMediaPillarData');
            this.logger.error(`[WebrtcGatewayService] getMediaPillarData sipwise error --  ${errorInfo.message}`);
            throw errorInfo.error;
        }
        //end sipwise case
    }

    /**
     * Method mediaPillarKeepAlivePolling
     * @private
     * @param {cmd} cmd [required] the keep alive command
     * @param {timer} timer [required] the keep alive timer
     * @memberof WebrtcGatewayService
     */
    private mediaPillarKeepAlivePolling(cmd, timer) {
        if (this.started) {
            switch (cmd) {
                case "START":
                    if (this.mediaPillarConnection && this.mediaPillarConnection.keepAlive_TO) {
                        clearTimeout(this.mediaPillarConnection.keepAlive_TO);
                        this.mediaPillarConnection.keepAlive_TO = null;
                    }
                    this.mediaPillarConnection.keepAlive_TO = setTimeout(() => { this.mediaPillarKeepAlive(); }, timer, 1);
                    break;
                case "STOP":
                    this.mediaPillarPollingSelected = false;
                    if (this.mediaPillarCallContext && this.mediaPillarConnection.keepAlive_TO) { clearTimeout(this.mediaPillarConnection.keepAlive_TO); }
                    this.mediaPillarConnection.keepAlive_TO = null;
                    break;
                default:
                    this.logger.error("[WebrtcGatewayService] mediaPillarKeepAlivePolling cmd error : " + cmd);
                    break;
            }
        }
    }

    /**
     * Method mediaPillarUserSelectAndPolling
     * @public
     * @param {boolean} selected [required] selected, true if MP is selected in nomadic menu
     * @memberof WebrtcGatewayService
     */
    public mediaPillarUserSelectAndPolling(selected) {
        if (this.started) {
            this.logger.log("[WebrtcGatewayService] mediaPillarUserSelectAndPolling : " + selected);
            if (selected) { //media pillar selected in user menu
                if (!this.mediaPillarPollingSelected) {
                    this.mediaPillarPollingSelected = true;
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                    this.mediaPillarKeepAlive();
                } else {
                    this.logger.log("[WebrtcGatewayService] mediaPillarUserSelectAndPolling polling already started");
                }
            }
            else {
                if (this.mediaPillarAlive) { //stop polling only if MP is alive
                    this.mediaPillarKeepAlivePolling("STOP", 0);
                    this.mediaPillarPollingSelected = false;
                }
            }
        }
    }

    /**
     * Method mediaPillarKeepAlive
     * @private
     * @memberof WebrtcGatewayService
     */
    public async mediaPillarKeepAlive(): Promise<void> {
        //this.logger.info("[WebrtcGatewayService] mediaPillarKeepAlive / suspend = " + this.mediaPillarKeepAliveSuspend);
        if (!this.mediaPillarKeepAliveSuspend) { //CRRAINB-3757
            const formerMediaPillarAliveState = this.mediaPillarAlive;
            try {
                await this.registerMediaPillarPool();
            }
            finally {
                if (this.mediaPillarAlive !== formerMediaPillarAliveState) {
                    if (this.mediaPillarAlive) {
                        this.logger.info("[WebrtcGatewayService] mediaPillarKeepAlive webrtcgateway is UP");
                    } else {
                        this.logger.info("[WebrtcGatewayService] mediaPillarKeepAlive webrtcgateway is DOWN");
                    }
                    this.eventService.publish("ON_WEBRTCGATEWAY_STATE_CHG", this.mediaPillarAlive);
                    this.logger.info("[WebrtcGatewayService] mediaPillarKeepAlive ON_WEBRTCGATEWAY_STATE_CHG");
                    this.keepAliveIterationCpt = 0; //CRRAINB-24110
                }
                if (this.mediaPillarAlive) { //this.mediaPillarAlive
                    if (this.TO_PILLAR_POLLING < TO_PILLAR_POLLING_MAX) {
                        this.TO_PILLAR_POLLING += TO_PILLAR_POLLING_STEP;
                    }
                    else {
                        this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MAX;
                    }
                    this.keepAliveIterationCpt = 0; //CRRAINB-24110
                }
                else { //not Alive
                    //CRRAINB-24110++
                    if (++this.keepAliveIterationCpt <= ITERATION_POLLING_SHORT) { //successive attempts against short dcnx
                        this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_SHORT;//re-try with short TO
                    } else { //definitly down despite N iterations then try later with normal TO
                        this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN;
                    }//CRRAINB-24110--
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                }
                if (this.isMediaPillarUserSelected() && this.mediaPillarAlive) {
                    this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
                }
            }
        } else {
            this.logger.info("[WebrtcGatewayService] KeepAlive suspended");
        }
    }

    /**
     * Method forceMPpooling
     * @public
     * @memberof WebrtcGatewayService
     */
    public forceMPpooling() {
        if (this.started) {
            this.logger.log("[WebrtcGatewayService] forceMPpolling ");
            this.TO_PILLAR_POLLING = TO_PILLAR_POLLING_MIN;
            this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
            this.eventService.publish("ON_WEBRTCGATEWAY_STATE_CHG", this.mediaPillarAlive);
        }
    }

    // ******************************************************************* //
    // ** HELPER FUNCTIONS                                               * //
    // ******************************************************************* //

    /**
     * Method isMediaPillarPoolConfigured
     * @private
     * @returns {boolean} true if at least one MP is configured (or the specified one when jid is given as parameter)
     * @memberof WebrtcGatewayService
     * @param MPJid when null check all MPs for at least one configured else check only the given MP jid
     */
    public isMediaPillarPoolConfigured(MPJid: string): boolean { //RQRAINB-6670
        let result = false;
        if (MPJid && this.mediaPillarConnection.mediaPillarStatesPool[MPJid]) {
            result = this.mediaPillarConnection.mediaPillarStatesPool[MPJid].mediaPillarConfigured;
        } else {
            const foundMPJid = this.mediaPillarConnection.mediaPillarJidArray.find((mediaPillarJid) => {
                return (this.mediaPillarConnection.mediaPillarStatesPool[mediaPillarJid]
                    && this.mediaPillarConnection.mediaPillarStatesPool[mediaPillarJid].mediaPillarConfigured === true);
            });
            if (foundMPJid) { result = true; }
        }
        return result;
    }

    /**
     * Method isMediaPillarPoolAlive
     * @private
     * @returns {boolean} true if at least one MP is alive (or the specified one when jid is given as parameter)
     * @memberof WebrtcGatewayService
     * @param MPJid when null check all MPs for at least one alive else check only the given MP jid
     */
    public isMediaPillarPoolAlive(MPJid: string): boolean { //RQRAINB-6670
        let result = false;
        if (MPJid && this.mediaPillarConnection.mediaPillarStatesPool[MPJid]) {
            result = this.mediaPillarConnection.mediaPillarStatesPool[MPJid].mediaPillarAlive;
        } else {
            result = this.mediaPillarConnection.mediaPillarJidArray.some((mediaPillarJid) => {
                return (this.mediaPillarConnection.mediaPillarStatesPool[mediaPillarJid]
                    && this.mediaPillarConnection.mediaPillarStatesPool[mediaPillarJid].mediaPillarAlive === true);
            });
        }
        return result;
    }

    /**
     * Method getAllMediaPillarJid
     * @public
     * @returns {string[]} all mediaPillar Jid
     * @memberof WebrtcGatewayService
     */
    public getAllMediaPillarJid(): string[] {
        return this.mediaPillarConnection.mediaPillarJidArray;
    }

    /**
     * Method getDefaultMediaPillarJid
     * @public
     * @returns {string} the default mediaPillar Jid
     * @memberof WebrtcGatewayService
     */
    public getDefaultMediaPillarJid(): string {
        //adapt hereafter the default jid selection strategy (for the moment the 1st one)
        let MPDefaultJid = this.mediaPillarConnection.mediaPillarJidArray[0];
        if (!MPDefaultJid) {
            MPDefaultJid = "";//no valid jid
        }
        return MPDefaultJid;
    }

    /**
     * Method getOnGoingMediaPillarJid
     * @public
     * @returns {string} the current mediaPillar Jid for active call (default otherwise)
     * @memberof WebrtcGatewayService
     */
    public getOnGoingMediaPillarJid(): string { //RQRAINB-6670
        let onGoingMPJid = this.getDefaultMediaPillarJid();
        if (this.mediaPillarCallContext && this.mediaPillarCallContext.onGoingCallMediaPillarJid && this.mediaPillarCallContext.onGoingCallMediaPillarJid !== "") {
            onGoingMPJid = this.mediaPillarCallContext.onGoingCallMediaPillarJid;
        }
        return onGoingMPJid;
    }

    /**
     * Method getMyMediaPillarRemoteExtension
     * @public
     * @returns {string} my mediaPillar remote extension ("" if not already obtained from server )
     * @memberof WebrtcGatewayService
     */
    public getMyMediaPillarRemoteExtension(): string {
        //RQRAINB-11813++ adding SDK use protections
        //if (!this.telephonyService.isSipWise()) return this.mediaPillarConnection ? this.mediaPillarConnection.remoteExtension : '';
        //Hybrid case
        if (!(this.contactService.userContact && this.contactService.userContact.systDeviceName === Contact.SIPWISE_RVCP)) return this.mediaPillarConnection ? this.mediaPillarConnection.remoteExtension : '';
        //SipWise case
        if (this.telephonyService) {
            const webrtc: string = this.telephonyService.telephonyServiceSipWise.getDeviceWithType('webrtc')?.deviceId;
            return webrtc ? webrtc : '';
        }
        return '';
    }

    /**
     * Method shouldUseMediaPillar
     * @public
     * @returns {boolean} true if media pillar should be used for PBX calls
     * @memberof WebrtcGatewayService
     */
    public shouldUseMediaPillar(): boolean {
        // let should = this.isMediaPillarAvailable() && this.isMediaPillarUserSelected();
        // if (typeof config.devMediaPillarEnabled === "undefined" || !config.devMediaPillarEnabled) {
        //     should = false;
        // }
        //return should;
        return false; //former 1PCC way
    }

    /**
     * Method isMediaPillarConfigured
     * @public
     * @returns {boolean} true if media pillar is configured for this user (configuration + rights)
     * @memberof WebrtcGatewayService
     */
    public isMediaPillarConfigured(): boolean {
        const configured = (this.mediaPillarConfigured && this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_WEBRTC_PSTN_CALLING));
        return (configured);
    }

    /**
     * Method isMediaPillarAvailable
     * @public
     * @returns {boolean} true if media pillar is available for this user (configuration + infra Up)
     * @memberof WebrtcGatewayService
     */
    public isMediaPillarAvailable(): boolean {
        let available = this.isMediaPillarConfigured();
        //available = available && this.telephonyService.started; //&& this.isMediaPillarEnabled;
        available = available && this.mediaPillarAlive;
        return (available);
    }

    /**
     * Method isMediaPillarUserSelected
     * @public
     * @returns {boolean} true if media pillar is selected (through Nomadic) for this user
     * @memberof WebrtcGatewayService
     */
    public isMediaPillarUserSelected(): boolean {
        let selected: boolean;
        if (!this.telephonyService.isSipWise()) { //not sipwise
            selected = (!this.telephonyService.nomadicObject.makeCallInitiatorIsMain && (this.telephonyService.getNomadicDestination() === this.getMyMediaPillarRemoteExtension()));
        } else { //sipwise case and webrtc device as current main device
            const current = this.telephonyService.telephonyServiceSipWise.getCurrentDevice();
            selected = (current && current.type === "webrtc");
        }
        return selected;
    }

    /**
     * Method isMediaPillarJid
     * @public
     * @param {string} fromJid the from jid
     * @returns {boolean} true if it is the media pillar Jid
     * @memberof WebrtcGatewayService
     */
    public isMediaPillarJid(fromJid: string): boolean {
        let indexMPinJid = -1; // presence&pos of "mp_" in jid
        //no jid provided!
        if (!fromJid) { return false; }
        indexMPinJid = fromJid.search("mp_"); //"mp_" must be at position 0
        return (indexMPinJid === 0);
    }

    /**
     * Method isMediaPillarCallCase
     * @public
     * @returns {boolean} true if Media pillar call
     * @memberof WebrtcGatewayService
     * check only the media pillar profile
     */
    public isMediaPillarCallCase(): boolean {
        const MP_callCase = this.isMediaPillarAvailable() && this.isMediaPillarUserSelected();
        return (MP_callCase);
    }

    /**
     * Method isMediaPillarSituation
     * @public
     * @param {object} call a call
     * @returns {boolean} true if Media pillar call situation
     * @memberof WebrtcGatewayService
     * In all cases test profile and Media pilar availability
     * If call provided check also that the call is linked to the centralized media pillar context
     * or if from jid is media pillar
     */
    public isMediaPillarCallSituation(call: any = null): boolean {
        let MP_situation = this.isMediaPillarCallCase();
        if (call) {
            if (call.callType === CallType.WEBRTC) {
                MP_situation = MP_situation && this.isMediaPillarJid(call.fullJid);
            }
            else { //CallType.PHONE
                if (this.telephonyService.getCalls().length <= 1 && this.mediaPillarCallContext.telephonyCallRefs.length <= 1) { //mono call case
                    switch (call.callStatus) {
                        case CallStatus.RINGING_INCOMMING:
                        case CallStatus.RINGING_OUTGOING:
                        case CallStatus.QUEUED_INCOMMING:
                        case CallStatus.DIALING:
                        case CallStatus.RELEASING:
                            break;
                        default:
                            if (call.mediaPillarCall) {
                                MP_situation = MP_situation && this.isInTelCallRefs(call);
                            }
                            else {
                                MP_situation = false;
                            }
                            break;
                    }
                }
                else { //multi calls case
                    switch (call.callStatus) {
                        case CallStatus.RINGING_INCOMMING:
                        case CallStatus.RINGING_OUTGOING:
                        case CallStatus.QUEUED_INCOMMING:
                        //case CallStatus.QUEUED_OUTGOING:
                        // Allow fallsthrough
                        case CallStatus.DIALING:
                        case CallStatus.RELEASING:
                            break;
                        default:
                            if (call.mediaPillarCall) {
                                MP_situation = MP_situation && (call.mediaPillarCall.telephonyCallRefs.length >= 1);
                                MP_situation = MP_situation && this.isInTelCallRefs(call);
                            }
                            else {
                                MP_situation = false;
                            }
                            break;
                    }
                }
            }
        }
        return (MP_situation);
    }

    /**
     * Method mediaPillaryseTheCall
     * @public
     * @param {object} telephonyCall the telephonic call ref
     * @param {object} webrtcCall  the web rtc call ref
     * @returns {string} operation status
     * @memberof WebrtcGatewayService
     */
    public mediaPillaryseTheCall(telephonyCall: any, webrtcCall: any): string {

        if (this.mediaPillarCallContext.callState !== MPCallState.Free) {
            //CRRAINB-27628++
            if (telephonyCall && this.telephonyService.oxoMultiCallMode() && this.mediaPillarCallContext.callState === MPCallState.TelCallReleasing
                && telephonyCall.callStatus === CallStatus.RINGING_INCOMMING) {
                this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall | special oxoMulticall situation with re nego of csta & web ref");
                telephonyCall.setMediaPillarCall(this.getMediaPillarCallContext());
                this.mediaPillarCallContext.callState = MPCallState.CallOnGoing;
                return "Update";
            } //CRRAINB-27628--
            if (telephonyCall && this.mediaPillarCallContext.updateContactFlag) { //CCRAINB-4634
                this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall | only Update"); //CRRAINB-5196
                telephonyCall.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context //CRRAINB-6167
                return "Update";
            }
            //CRRAINB-6167++
            if (telephonyCall && this.mediaPillarCallContext.callState === MPCallState.TelCallReleasing && telephonyCall.callStatus === CallStatus.RINGING_INCOMMING) { //CCRAINB-6167
                this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall |  transfer managed as Update");
                telephonyCall.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
                this.mediaPillarCallContext.callState = MPCallState.CallOffer;
                return "Update";
            }//CRRAINB-6167--
            if (webrtcCall && this.mediaPillarCallContext.callState !== MPCallState.WaitWebrtc) {
                this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall | only Ignore"); //CRRAINB-5196
                return "Ignore";
            }
        }

        let status = "";
        if (!telephonyCall && !webrtcCall) {
            return "NoCallRef";
        }
        if (telephonyCall && webrtcCall) { //normally one ref at a time
            return "toManyCallRef";
        }
        if (telephonyCall) {
            switch (this.mediaPillarCallContext.callState) {
                case MPCallState.Free:
                    this.mediaPillarCallContext.callState = MPCallState.WaitWebrtc;
                    status = "Ok";
                    break;
                case MPCallState.WaitTelephony:
                    this.mediaPillarCallContext.callState = MPCallState.CallOffer;
                    status = "Ok";
                    break;
                case MPCallState.CallOffer: //CRRAINB-26420
                    this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall | in callOffer received again incomingCall then ignore");
                    status = "Ignore";
                    break;
                case MPCallState.CallActive: //CRRAINB-19939++
                case MPCallState.WebCallReleasing:
                    if (telephonyCall.callStatus === CallStatus.RINGING_INCOMMING) {
                        status = "Ignore";
                    }
                    break;
                    //CRRAINB-19939--

                case MPCallState.WaitWebrtc: //CRRAINB-6251++
                    if (this.mediaPillarCallContext.telephonyCallRefs.length > 1) {
                        this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall but 2nd incomming in WaitWebrtc| only Ignore");
                        telephonyCall.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
                        status = "Ignore";
                    } else if (this.telephonyService.isSipWise()) {
                        this.logger.info("[WebrtcGatewayService] multiple mediaPillaryseTheCall but sipwise case | only Ignore");
                    } else {
                        this.mediaPillarCallContext.callState = MPCallState.AnomalyCCS;
                        status = "Ano_inTel_contextCallState_" + this.mediaPillarCallContext.callState;
                        this.logger.info("[WebrtcGatewayService] mediaPillaryseTheCall again in WaitWebrtc");
                    }
                    break; //CRRAINB-6251--
                default: //anomaly
                    this.mediaPillarCallContext.callState = MPCallState.AnomalyCCS;
                    status = "Ano_inTel_contextCallState_" + this.mediaPillarCallContext.callState;
                    break;
            }
            if (status === "Ok") {
                this.putAsMasterTelCallRefs(telephonyCall); //set as main telcallref
                telephonyCall.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
            }
        }
        else {
            if (webrtcCall) {
                this.mediaPillarCallContext.webrtcCallRef = webrtcCall;
                switch (this.mediaPillarCallContext.callState) {
                    case MPCallState.Free:
                        this.mediaPillarCallContext.callState = MPCallState.WaitTelephony;
                        status = "Ok";
                        break;
                    case MPCallState.WaitWebrtc:
                        this.mediaPillarCallContext.callState = MPCallState.CallOffer;
                        status = "Ok";
                        break;
                    default: //anomaly
                        this.mediaPillarCallContext.callState = MPCallState.AnomalyCCS;
                        status = "Ano_inWrtc_contextCallState_" + this.mediaPillarCallContext.callState;
                        break;
                }
                if (status === "Ok") {
                    webrtcCall.setMediaPillarCall(this.getMediaPillarCallContext()); //link the call to MP context
                }
            }
        }

        return status;
    }

    /**
     * Method getMediaPillarCallContext
     * @public
     * @returns {object} the media pillar call context
     * @memberof WebrtcGatewayService
     * Allow to link a call (tel or webrtc) to the media pillar call context
     * @param call
     */
    public getMediaPillarCallContext(call: CallRB = null): any {

        if (!call || !this.telephonyService.isSipWise()) {
            return this.mediaPillarCallContext;
        }

        //Sipwise usecase
        //search the context containing the given call
        const foundContext = this.mediaPillarCallContexts.find((mediaPillarCallContext: MediaPillarCallContext) => {
            return (mediaPillarCallContext.telephonyCallRefs
                && mediaPillarCallContext.telephonyCallRefs.length
                && mediaPillarCallContext.telephonyCallRefs[0].id === call.id) // search tel call
                || (mediaPillarCallContext.webrtcCallRef
                    && mediaPillarCallContext.webrtcCallRef.id === call.id); // search webrtc call
        });

        if (foundContext) {
            //this.logger.debug("[WebrtcGatewayService] CTX getMediaPillarCallContext for " + call + " returns context id " + foundContext.id);
        } else {
            this.logger.error("[WebrtcGatewayService] CTX getMediaPillarCallContext for " + call + " returns context not found");
        }
        return foundContext;
    }

    /**
     * Method isMediaPillarOutgoingCall
     * @public
     * @param
     * @returns {boolean} true if outgoing 3PCC call
     * @memberof WebrtcGatewayService
     * return true if the mediaPillarCallContext is used for an outgoing 3PCC call
     */
    public isMediaPillarOutgoingCall() {
        let compatibleCallState: boolean;
        switch (this.mediaPillarCallContext.callState) {
            case MPCallState.WaitWebrtc:
            case MPCallState.WaitTelephony:
            case MPCallState.CallOffer:
            case MPCallState.CallOnGoing:
            case MPCallState.CallActive:
            case MPCallState.CallActiveNoWebMedia:
            case MPCallState.TelCallReleasing: //???
            case MPCallState.WebCallReleasing: //???
                compatibleCallState = true;
                break;
            default:
                compatibleCallState = false;
                break;
        }
        return (compatibleCallState && this.mediaPillarCallContext.isOutgoingCall3PCC);
    }

    /**
     * Method isMediaPillarActivableCall
     * @private
     * @param
     * @returns {boolean} true if
     * @memberof WebrtcGatewayService
     * return true if
     */
    /*
    private isMediaPillarActivableCall() {
        var compatibleCallState: boolean;
        switch (this.mediaPillarCallContext.callState) {
            case MPCallState.WaitWebrtc:
            case MPCallState.CallOffer:
                compatibleCallState = true;
                break;
            default:
                compatibleCallState = false;
                break;
        }
        return (compatibleCallState);
    };
    */

    /**
     * Method isMediaPillarReleasableCall
     * @private
     * @param
     * @returns {boolean} true if
     * @memberof WebrtcGatewayService
     * return true if
     */
    private isMediaPillarReleasableCall() {
        let compatibleCallState: boolean;
        switch (this.mediaPillarCallContext.callState) {
            case MPCallState.AnomalyCCS:
            case MPCallState.Unknow:
                compatibleCallState = false;
                break;
            default:
                compatibleCallState = true;
                break;
        }
        return (compatibleCallState);
    }

    /**
     * Method mediaPillarReleaseWebrtcCall
     * @public
     * @memberof WebrtcGatewayService
     * release only webrtc call (not tel part)
     * @param mediaPillarCallContext
     */
    public mediaPillarReleaseWebrtcCall(mediaPillarCallContext: MediaPillarCallContext) {
        //Free web call //CRRAINB-3500
        if (mediaPillarCallContext.webrtcCallRef) {
            mediaPillarCallContext.webrtcCallRef.mediaPillarCall = null; //cut the link from call to mediapillar call context
            this.webrtcP2PService.releaseCall(mediaPillarCallContext.webrtcCallRef);
        }
    }

    /**
     * Method timeOutCallActiveNoWebMedia
     * @public
     * @memberof WebrtcGatewayService
     * @param mediaPillarCallContext
     */
    public timeOutCallActiveNoWebMedia(mediaPillarCallContext) {
        //CRRAINB-3500
        this.logger.info("[WebrtcGatewayService] timeOutCallActiveNoWebMedia");
        if (mediaPillarCallContext.callState === MPCallState.CallActiveNoWebMedia) {
            this.mediaPillarCallTerminator(mediaPillarCallContext);
            this.mediaPillarKeepAlive();
            this.logger.info("[WebrtcGatewayService] timeOutCallActiveNoWebMedia noWebMedia then release calls if existing & force keepalive");
        }
    }

    /**
     * Method mediaPillarCallTerminator
     * @public
     * @memberof WebrtcGatewayService
     * Deep release of media pillar context including release of associated calls
     * @param mediaPillarCallContext
     */
    public mediaPillarCallTerminator(mediaPillarCallContext: MediaPillarCallContext = null) {
        if (!mediaPillarCallContext) { mediaPillarCallContext = this.mediaPillarCallContext; }

        //Free the tel calls
        if (mediaPillarCallContext.telephonyCallRefs.length !== 0) {
            mediaPillarCallContext.telephonyCallRefs.forEach((callRef) => {
                if (callRef !== null && callRef.type.value === CallType.PHONE) {
                    callRef.mediaPillarCall = null; //cut the link from associated call to this mediapillar call context
                    this.telephonyService.releaseCall(callRef); //and release call
                    this.logger.info("[WebrtcGatewayService] mediaPillarCallTerminator | release call" + callRef);
                    if (this.telephonyService.isSipWise()) { //CRRAINB-37068
                        this.telephonyService.clearCall(callRef);
                    }
                }
            });
        }
        this.mediaPillarReleaseWebrtcCall(mediaPillarCallContext); //Free web call
        this.releaseMediaPillarCallContext(mediaPillarCallContext); //free mediapillar call context
        this.resetEvtAutomaton(); //reset eventing automaton
        this.TO_PILLAR_POLLING += TO_PILLAR_POLLING_MIN;
        this.mediaPillarKeepAlivePolling("START", this.TO_PILLAR_POLLING);
        this.logger.warn("[WebrtcGatewayService] mediaPillarCallTerminator | Full cleaning");
    }

    // ************************************************************ //
    // * MEDIAPILLAR USER                                         * //
    // ************************************************************ //

    /**
     * @param jid
     */
    private createMediaPillarContact(jid: string): any {
        this.logger.info("[webrtcGatewayService] createMediaPillarContact -- " + jid);
        const contact = this.contactService.createBasicContact(jid);
        contact.displayName = "mediaPillar";
        contact.avatar = new Image();

        if (config.sdk) {
            // unknownUser.jpeg is not available in SDK, create exception to avoid error
            contact.avatar.src = "";
        } else {
            contact.avatar.src = "/assets/skins/rainbow/images/conversations/unknownUser.jpeg";
        }

        //create also contact with bare jid for webrtc telephony (required in acceptProposition / incomingCall methods when answering mediapillar webrtc call)
        if (this.xmppService.getResourceFromJid(jid)) {
            this.contactService.createBasicContact(this.xmppService.getBareJidFromJid(jid));
        }
        return contact;
    }

    // *************************************************************/
    // ** MEDIA ACCESS                                             */
    // *************************************************************/

    public muteAudio(pbxCall, muted, conversation) {
        if (!pbxCall) {
            this.logger.error("[webrtcGatewayService] muteAudio - trying to mute a non existing call !");
            return;
        }

        if (this.getMediaPillarCallContext(pbxCall)) {
            const webrtcCall = this.getMediaPillarCallContext(pbxCall).webrtcCallRef;
            if (webrtcCall) {
                const session = this.xmppService.connection.jingle.sessions[webrtcCall.id];
                if (session) {
                    session.muteAudio(muted);

                    webrtcCall.isMuted = muted;
                    if (conversation) {
                        conversation.isMutedAudio = muted;
                        conversation?.sendEvent(RBEvent.create(ConversationEvents.RAINBOW_CONVERSATION_CALL_UPDATE, { call: pbxCall }));
                    }

                    //update the state of the PBX call
                    pbxCall.isMuted = muted;

                    this.logger.info("[webrtcGatewayService] muteAudio conversation: " + (conversation ? conversation.id : "undefined") + " / pbxcall: " + pbxCall.id + "-- " + (muted ? "mute" : "unumute"));

                    //send event
                    const eventData = { call: pbxCall };
                    if (conversation) {
                        eventData["conversation"] = conversation;
                    }
                    this.eventService.publish("ON_CONVERSATION_CALL_MUTED_UPDATED_EVENT", eventData);

                    //send update to the desktop app
                    this.eventService.publish("ON_MPMEDIA_CONVERSATION_CALL_UPDATED_EVENT", {
                        call: webrtcCall
                    });
                }
                else {
                    this.logger.error("[webrtcGatewayService] muteAudio " + pbxCall.id + " -- failure: no session");
                }

                this.conversationService.updateCallCapabilities(pbxCall);
            }
        }
    }

    private getActivePbxCall() {
        if (this.isMediaPillarCallCase() && this.mediaPillarCallContext) {
            return this.mediaPillarCallContext.telephonyCallRefs.find((pxbCall) => { return pxbCall.callStatus === CallStatus.ACTIVE; });
        }
        return null;
    }

    // *************************************************************/
    // ** CALL MANAGEMENT                                         */
    /**
     * @param call
     */
    public async answerCall(call: CallRB): Promise<void> {
        const mediaPillarCall = call.getMediaPillarCall();

        this.logger.info("[WebrtcGatewayService] [TEL][GW] -- answerCall call " + call + " -- webrtcCall " + ((mediaPillarCall && mediaPillarCall.webrtcCallRef) ? mediaPillarCall.webrtcCallRef : "no context or no webrtc call"));

        if (this.isMixPstnWebRtcAllowed) {
            //check if any active webrtc call and put it on hold before we abswer that one
            this.eventService.publish("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD");
        }

        if (this.telephonyService.oxoMultiCallMode() && call.callType === CallType.PHONE && !call.makecallCtiExtern) { //RQ6394++
            this.logger.info("[WebrtcGatewayService] -- answerCall call id " + call.id + " -- OXO specific mng");
            if (call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.RINGING_INCOMMING) {
                this.logger.info("[WebrtcGatewayService] -- answerCall -- by telephony service for call : " + call.id);
                this.putAsMasterTelCallRefs(call);//becomes the master callRef
                this.mediaPillarCallContext.callOwnerForOxoMultiClientFlag = true; //RQ6394 flag the context as oxo case call owner
                this.mediaPillarCallContext.callState = MPCallState.CallOffer; // CRRAINB-29649
                try {
                    await this.telephonyService.answerCall(call);
                }
                catch (error) {
                    this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                    throw error;
                }
                return;
            }
        }//RQ6394--

        if (mediaPillarCall && (mediaPillarCall.webrtcCallRef?.callStatus === CallStatus.ANSWERING || call.callStatus === CallStatus.ANSWERING)) {
            // Do nothing : the answer is in progress for this call
            this.logger.info("[WebrtcGatewayService] -- answerCall -- ignored because answer is in progress for this call : " + call);
        }
        else if (mediaPillarCall && (this.webrtcP2PService.existMediaPillarAnsweringCall() || this.telephonyService.getCalls(CallStatus.ANSWERING).length)) {
            // An answer of one MP call is in progress on this user for another call (possible in case of Hub)
            // we differ the answerCall request
            // Note: on Hub system, we must not allow two simultaneous answerCall service requests on 2 different incoming calls: this would result in 2 active calls
            this.logger.info("[WebrtcGatewayService] -- answerCall -- differed for call: " + call.id + " because answer is in progress for another call");
            setTimeout(() => {
                if (call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.RINGING_INCOMMING) {
                    this.logger.info("[WebrtcGatewayService] -- answerCall -- After timeout : try again to answer ... " + call);
                    // we call back the answerCall() method of callService and not this.answerCall() because we need to display the conversation of this call, which is managed by callService
                    const callService = this.mainService.inject<WebrtcP2PService>(CALL_SVC);
                    if (callService) callService.answerCall(call);
                } else {
                    this.logger.debug("[WebrtcGatewayService] -- answerCall -- After timeout : no more call to answer " + call);
                }
            }, 500);
            throw new Error("Differed because another call answer is in progress");
        }
        else if (mediaPillarCall && mediaPillarCall.webrtcCallRef && mediaPillarCall.webrtcCallRef.callStatus === CallStatus.RINGING_INCOMMING) {
            // First do some action on active call if necessary:
            // In sipwise usecase, we first hold the active call , before answering to the webrtc incoming call
            call.noIncomingPopup = true;
            await this.actionOnActiveCallIfNecessary(call);
            // answer to the webrtc incoming call
            this.logger.info("[WebrtcGatewayService] -- answerCall -- send webrtc proceed for call : " + call.id);
            this.webrtcP2PService.answerCall(mediaPillarCall.webrtcCallRef, 'audio');
            call.isMPAudioOwner = true;
        }
        else if (call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.RINGING_INCOMMING) {
            this.logger.info("[WebrtcGatewayService] -- answerCall -- by telephony service for call : " + call.id);
            try {
                await this.telephonyService.answerCall(call);
            }
            catch (error) {
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                throw error;
            }
        }
        else {
            this.logger.info("[WebrtcGatewayService] -- answerCall -- Anomaly media Pillar : answerAudioAction but no webrtcCallref & no 2nd call");
        }
    }

    public async actionOnActiveCallIfNecessary(incomingcall: CallRB): Promise<any> {

        if (this.isMixPstnWebRtcAllowed) {
            //check if any active webrtc call and put it on hold before we retrieve that one
            this.eventService.publish("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD");
        }

        // First release an ongoing outgoing call, if any
        const ongoingOutgoingCall = this.telephonyService.getOngoingOutgoingCall();
        if (ongoingOutgoingCall && ongoingOutgoingCall.id !== incomingcall.id) {
            return this.telephonyService.releaseCall(ongoingOutgoingCall);
        }

        // In sipwise mode, first hold the active call , if any
        if (this.telephonyService.isSipWise()) {
            const telActiveCall = this.telephonyService.getActiveCall();
            if (telActiveCall && telActiveCall.id !== incomingcall.id) {
                return this.telephonyService.holdCall(telActiveCall);
            }
        }
        return undefined;
    }

    public releaseCall(call: CallRB): void {
        const mediaPillarCall = call.getMediaPillarCall();

        if (mediaPillarCall && mediaPillarCall.webrtcCallRef) {
            if (call.callType === CallType.PHONE) {
                this.logger.info("[WebrtcGatewayService] [TEL][GW] -- releaseCall MP PHONE -- " + call.id);
                if (mediaPillarCall.telephonyCallRefs.length <= 1) { // CRRAINB-27356
                    this.logger.info("[WebrtcGatewayService] -- rejectCall MP webrtc call -- " + mediaPillarCall.webrtcCallRef);
                    //TEMPORARY FOR RQRAINB-6021
                    this.webrtcP2PService.terminateCall(mediaPillarCall.webrtcCallRef, "", call.isHuntingGroupCallSipWise() || (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_CALL_BUSY) && this.myContact.oxeBusyCapability)); // release web part for CRRAINB-27356
                    // we sould send the reject message to the other participant only for sipwise hg calls (not for hybrid calls and sipwise simple calls) or OXE busy capability
                }
                //in all cases, release tel
                this.telephonyService.releaseCall(call)
                    .catch(() => {
                        call.setStatus(CallStatus.ENDED);
                        this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                    });
            }
            else {
                this.logger.info("[WebrtcGatewayService] [TEL][GW] -- releaseCall MP WEBRTC -- " + call.id);
                this.webrtcP2PService.terminateCall(mediaPillarCall.webrtcCallRef);
            }
        }
        else {
            this.logger.info("[WebrtcGatewayService] [TEL][GW] releaseCall | Anomaly media Pillar : declineAction but no webrtcCallref for call = " + call.id);
            this.telephonyService.releaseCall(call)
                .catch(() => {
                    call.setStatus(CallStatus.ENDED);
                    this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                });
        }
    }

    //CR25504-24670 & RQ6572++
    private specialCaseNotComputerModeButMPCall(telCall: CallRB): CallRB {
        if (!telCall) {
            this.logger.info("[WebrtcGatewayService] specialCaseNotComputerModeButMPCall | Anomaly no tel call provided");
            return;
        }
        const incomingMPWebrtcCall = this.webrtcP2PService.getMPWebrtcAudioCallByState(CallStatus.RINGING_INCOMMING);
        if (!incomingMPWebrtcCall) {
            this.logger.info("[WebrtcGatewayService] specialCaseNotComputerModeButMPCall | no valid MP webrtcCall found");

            return null;
        }
        //we found a MP webrtc call candidate (with Jid as media pillar but not yet managed in MP context because of not in computer mode)
        //we ("probably!)are in sipWise single step transfer situation
        //Despite we are not in computer mode we have to manage de MP webrtc call as a valide MP call
        //Sic that's a pity hack for incredible "meli-melo" case !!
        const MPContext = this.getMediaPillarCallContext();
        //link the context to the found webrtcCall
        MPContext.webrtcCallRef = incomingMPWebrtcCall;
        //link the calls to MP context
        incomingMPWebrtcCall.setMediaPillarCall(MPContext);
        telCall.setMediaPillarCall(MPContext);
        //hack the context state
        MPContext.callState = MPCallState.CallOffer;
        //tags the webrtc call as special abomination single step transfer
        //to not filter the next evt !!! outch !!!
        incomingMPWebrtcCall.setMixtWebTelSingleStepTrans(true);
        //disable TO
        this.stopWaitTelephonyTimeOut();
        this.stopCallStateTimeOut();
        //because of single step transfer "try !" to disable popup
        telCall.noIncomingPopup = true;

        this.logger.debug("[WebrtcGatewayService] specialCaseNotComputerModeButMPCall | hack the telCall: " + telCall.id + " / webCall: " + incomingMPWebrtcCall.id);

        return incomingMPWebrtcCall;

        //then pray that everything goes well in that abomination case :-) !!
    }//CR25504-24670 & RQ6572--
}
